import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import Papa from "papaparse";
import { BRAND, track as baseTrack } from "./shared";
import { captureAttribution, trackBooking } from "./glocalTracking";
import BookingModal, { getDiscount } from "./BookingModal";
import SessionBooking from "./SessionBooking";
import { useEvents, EVENT_PREFIX } from "./events/data";
import { upcomingEvents } from "./events/schedule";
import { EventsPopup, EventsStrip, EventsTicker } from "./events/EventsPopup";

// -------- TRACCIAMENTO PRENOTAZIONI -> GOOGLE SHEET ------------------------
// Salva UTM/ref del link d'ingresso (Instagram, Meta Ads, QR…) e, a ogni
// start_booking, manda anche una riga al foglio via Apps Script (glocalTracking.js).
captureAttribution();
const track = (name, params = {}) => {
  baseTrack(name, params);
  if (name === "start_booking") {
    trackBooking(params.card, { method: params.method || "", from: params.from || "" });
  }
};

// Portale locali / admin (/partner): caricato solo quando serve, così la
// libreria Supabase non appesantisce l'app dei turisti.
const PartnerPortal = lazy(() => import("./events/PartnerPortal"));
const isPartnerRoute = () => window.location.pathname.replace(/\/+$/, "") === "/partner";

// I link di invito / accesso inviati da Supabase possono atterrare sulla home
// dell'app: in quel caso li giriamo al portale /partner, che completa il login.
if (!isPartnerRoute() && /(access_token=|type=(invite|magiclink|recovery)|[?&]code=|error_description=)/.test(window.location.hash + window.location.search)) {
  window.location.replace("/partner" + window.location.search + window.location.hash);
}

/* ============================================================================
   GLOCAL — web app (mobile-first)
   ----------------------------------------------------------------------------
   Si apre direttamente sulle sezioni per INTERESSE:
   Cibo · Bere · Musica dal vivo · Natura · Musei, arte e cultura · Shopping.
   In cima, sotto la barra "Interessi", il banner "Benvenuto a Bologna" con le
   Due Torri. Poi il blocco "100% Made in Bo" e, in ogni sezione, in cima,
   il blocco "Bologna doc" (i classici col consiglio da local) — le righe
   con doc = "yes".
   Due tab: Home · Itinerario. Nessuna welcome screen, nessuna profilazione, no mappa.
   Dati dal Google Sheet (CSV) + prenotazioni via Formspree.

   Colonne foglio:
   id | interests | title_it | title_en | desc_it | desc_en | image | images |
   bookable | price | location | address | lat | lng | contact | doc | tip_it | tip_en |
   place_id | orari | bookings_week | menu | menu_it | menu_en | booking | booking_url | phone | sconto
   - sconto: (facoltativo) solo il numero, es. 10 = -10% prenotando con Glocal.
             Vuoto = nessuno sconto. Compilare solo se concordato col locale.
   (menu/booking/booking_url/phone: vedi il blocco "MENU & PRENOTAZIONE" sotto)

   - interests: una o più tra food, drink, music, nature, museums, shopping
                (virgola). Determina in quale/quali sezioni appare la card:
                un bar con musica dal vivo = "drink, music" compare in entrambe.
   - doc:       "yes" -> la card entra nel blocco "Bologna doc" della/e sua/e
                sezione/i (un classico da vedere). Altro/vuoto -> card normale.
   - tip_it/tip_en: (facoltativo) il consiglio da local mostrato sulle card doc.
   - bookable:  "yes" -> pulsante Prenota. Altro/vuoto -> nascosto.
   - images:    URL extra separati da virgola (galleria nel dettaglio).
   - address:   indirizzo cliccabile (apre Google Maps).
   - place_id:  Google Place ID, usato dallo script Apps Script per popolare "orari".
   - orari:     orari settimanali sincronizzati da Google (una riga per giorno,
                separate da \n, es. "lunedì: 18:00–01:00"). Facoltativo: se
                vuoto, il blocco orari non viene mostrato.
   - bookings_week: (facoltativo, solo per righe con bookable = yes) numero
                inserito A MANO nel foglio da Giulio. Alimenta il banner
                "X persone hanno prenotato X questa settimana": vuoto o 0 =
                quel posto non entra in rotazione nel banner.
   ============================================================================ */

// BRAND (colori) e track (Google Analytics) sono in src/shared.js

// Le SEZIONI dell'app = interessi. L'ordine qui è l'ordine in Home.
const SECTIONS = [
  { id: "food",     it: "Cibo",                  en: "Food",             icon: "/icons/food.png" },
  { id: "drink",    it: "Bere",                  en: "Drinks",           icon: "/icons/drink.png" },
  { id: "music",    it: "Musica dal vivo",       en: "Live music",       icon: "/icons/music.png" },
  { id: "nature",   it: "Natura",                en: "Nature",           icon: "/icons/nature.png" },
  { id: "museums",  it: "Musei, arte e cultura", en: "Museums & culture",icon: "/icons/museums.png" },
  { id: "shopping", it: "Shopping",              en: "Shopping",         icon: "/icons/shopping.png" },
];

// -------- CONTENUTI dal Google Sheet pubblicato come CSV --------------------
const CSV_URL = "https://docs.google.com/spreadsheets/d/e/2PACX-1vTDteVaj56DqRzerlc3EP5YqmpeQYOydBBadXfBE0CozUnO3lcTRN6zrWSghznYtBd5aWYp8D2ALcbL/pub?gid=1251495453&single=true&output=csv";

const SPOTIFY_PLAYLIST_ID = "0PtXUavyTbZMVzvGrgHLKt";
const SPOTIFY_EMBED_URL = `https://open.spotify.com/embed/playlist/${SPOTIFY_PLAYLIST_ID}?utm_source=generator&theme=0`;
const SPOTIFY_LINK_URL = `https://open.spotify.com/playlist/${SPOTIFY_PLAYLIST_ID}?si=d13dfa5fe7ab4985`;

// -------- BADGE ROTANTE — Nettuno / tortellino / Due Torri, si alternano con un "flip" --
const ROTATOR_ITEMS = [
  { src: "/icons/badge-nettuno.png", alt: "Il Nettuno" },
  { src: "/icons/badge-tortellino.png", alt: "Un tortellino" },
  { src: "/icons/badge-duetorri.png", alt: "Le Due Torri" },
];

// -------- GOOGLE ANALYTICS 4 -----------------------------------------------
const GA_ID = "G-SDH5FJLQSP";
// carica lo script GA una sola volta
function initGA() {
  if (typeof window === "undefined" || window.__gaLoaded) return;
  window.__gaLoaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag("js", new Date());
  gtag("config", GA_ID);
}
// Converte il prezzo scritto sul foglio (es. "15€", "A partire da 40€") in una
// fascia a simboli di euro (€ / €€ / €€€), come su tante app di recensioni.
// Se nel testo non c'è un numero (es. "Gratis", "Su invito") lo lascia com'è.
function priceSymbol(priceStr) {
  if (!priceStr) return priceStr;
  const match = String(priceStr).replace(",", ".").match(/\d+(\.\d+)?/);
  if (!match) return priceStr;
  const num = parseFloat(match[0]);
  if (Number.isNaN(num)) return priceStr;
  if (num <= 15) return "€";
  if (num <= 35) return "€€";
  return "€€€";
}

// Prezzo da mostrare per un posto. Esperienze (madeinbo = yes) col numero
// nel foglio -> prezzo vero con "da" davanti ("Da 40€" / "From €40"),
// perché è il prezzo a persona di un'esperienza. Tutti gli altri posti ->
// fascia €/€€/€€€. Testi senza numero ("Gratis", "Su invito") restano uguali.
function displayPrice(place, lang) {
  const raw = String(place?.price || "").trim();
  if (!raw) return "";
  if (String(place.madeinbo || "").trim().toLowerCase() === "yes") {
    const m = raw.match(/\d+(?:[.,]\d{1,2})?/);
    if (m) return lang === "en" ? `From €${m[0]}` : `Da ${m[0]}€`;
    return raw;
  }
  return priceSymbol(raw);
}


/* ------------------------------- I18N ------------------------------------- */
const T = {
  it: {
    loading: "Caricamento…",
    pickTitle: "Cosa ti interessa fare a Bologna?",
    pickSub: "Scegli uno o più temi. Ti mostriamo i posti migliori scelti per te, come fossimo il tuo amico del posto.",
    pickCta: "Vedi i risultati", pickHint: "Scegline almeno uno",
    editInterests: "Interessi",
    welcomeTitle: "Benvenuto a Bologna",
    welcomeSub: "Le esperienze scelte per te, come un amico del posto",
    homeTitle: "Cosa ti va di fare a Bologna?",
    filterAll: "Tutti",
    experiencesTitle: "Esperienze", experienceBadge: "Esperienza",
    freeByInvite: "Gratis · su invito", free: "Gratis",
    docTitle: "Bologna doc", docSub: "I classici, col consiglio di un local",
    madeTitle: "100% Made in Bo", madeSub: "Esperienze autentiche, nate qui",
    pickOrGuide: "Oppure", guideCta: "Visualizza la guida gratis",
    guideBannerKicker: "Guida gratuita",
    guideBannerTitle: "La guida definitiva a Bologna in {n} giorni",
    guideBannerStops: "posti · giorno per giorno",
    guideBannerCta: "Guarda",
    recommendedBy: "Consigliato da",
    itinFloatingCta: "Itinerario",
    guideTitle: "La guida definitiva", guideSub: "Tre giorni a Bologna, già organizzati.",
    guideBack: "Torna alle card",
    guideGateTitle: "Sblocca la guida", guideGateSub: "Lasciaci la tua email, è gratis.",
    guideUnlock: "Sblocca",
    guideConsent: "Accetto il trattamento della mia email, secondo la",
    installTitle: "Porta Glocal sul telefono!",
    installSub: "Come un'app vera, in 10 secondi ✨",
    installIosStep1: "Tocca qui sotto", installIosStep1Detail: "l'icona Condividi",
    installIosStep2: "Poi tocca", installIosStep2Detail: "\"Aggiungi alla schermata Home\"",
    installAndroidStep1: "Tocca i tre puntini", installAndroidStep1Detail: "in alto nel browser",
    installAndroidStep2: "Poi tocca", installAndroidStep2Detail: "\"Aggiungi alla schermata Home\"",
    installCta: "Installa ora, un tap!", installGotIt: "Fatto, ho capito", installLater: "Più tardi",
    soundtrackTitle: "La colonna sonora di Bologna",
    soundtrackSub: "La playlist scelta da chi ci vive, per il tuo viaggio",
    soundtrackOpen: "Apri in Spotify",
    betaBar: "🚧 Versione in anteprima",
    qfTitle: "Come ti stai trovando?", qfSub: "Bastano 10 secondi (facoltativo)",
    qfRatingLabel: "Come valuti l'esperienza finora?",
    qfImproveLabel: "Cosa miglioreresti?", qfImprovePlaceholder: "Scrivi qui (facoltativo)…",
    qfExtraLabel: "C'è qualcosa che ti aspettavi di trovare e non hai trovato?", qfExtraPlaceholder: "Scrivi qui (facoltativo)…",
    qfSkip: "Salta", qfSend: "Invia", qfSending: "Invio…",
    qfThanks: "Grazie mille!",
    cookieText: "Usiamo cookie tecnici e di statistica per capire come viene usata l'app e migliorarla.",
    cookieOk: "Ho capito", cookiePolicy: "Privacy",
    localTip: "Il consiglio del local", localTipsBtn: "Local tips",
    localTipTitle: "Consiglio locale",
    emptySection: "Presto nuovi contenuti in questa sezione.",
    book: "Prenota gratuitamente", bookDates: "Vedi date e prenota", addItinShort: "Itinerario", inItinShort: "Aggiunto",
    addItin: "Aggiungi all'itinerario", inItin: "Nell'itinerario",
    of: "di",
    tabHome: "Home", tabItin: "Itinerario",
    itinTitle: "Il tuo itinerario",
    itinReminderOne: "posto nel tuo itinerario", itinReminderMany: "posti nel tuo itinerario",
    itinReminderCta: "Vedi il percorso",
    itinEmpty: "Aggiungi luoghi ed esperienze dalla Home per costruire il tuo itinerario.",
    remove: "Rimuovi", clearAll: "Svuota", goHome: "Vai alla Home",
    openInMaps: "Apri in Google Maps", shareWa: "Condividi su WhatsApp",
    shareCardText: "Guarda {title} su Glocal:",
    booking: "Prenota", name: "Nome e cognome", email: "Email",
    people: "Persone", date: "Data",
    bookingSubtitle: "Prenota in pochi click. Nessun pagamento, nessun impegno. Riceverai una conferma quando la tua prenotazione sarà effettiva.",
    send: "Invia richiesta", sending: "Invio…",
    thanks: "Richiesta inviata", thanksSub: "Non è ancora una conferma: il local ti risponde via email entro 24 ore.",
    askLocalKicker: "Vuoi supporto per il tuo viaggio a Bologna?", askLocal: "Chiedi ad un local", askLocalMsg: "Ciao Glocal! Sono a Bologna e cerco un consiglio: ",
    whatsapp: "Scrivi su WhatsApp", close: "Chiudi", back: "Indietro", required: "Compila i campi obbligatori.",
    openNow: "Aperto ora", closedNow: "Chiuso ora", closesAt: "chiude alle", opensAt: "apre alle",
    hoursTitle: "Orari", hoursSynced: "Orari sincronizzati da Google", open24h: "Aperto 24 ore su 24",
    abandonedTitle: "Ancora indecis*? 👀",
    abandonedBody: "Non hai ancora prenotato nessuna esperienza. Dare un'occhiata è gratis e senza impegno.",
    abandonedCta: "Scopri le esperienze",
    abandonedClose: "Continua a guardare",
    socialProofWeek: "persone hanno prenotato questa settimana",
    menuBtn: "Vedi il menu", menuTitle: "Menu",
    bookOnline: "Prenota online", bookCall: "Chiama", bookWa: "WhatsApp",
    openNewTab: "Apri in una nuova scheda",
    embedHint: "Non si carica? Aprilo in una nuova scheda.",
    waBookText: "Ciao! Vi scrivo da Glocal, vorrei prenotare da {title}.",
  },
  en: {
    loading: "Loading…",
    pickTitle: "What would you like to do in Bologna?",
    pickSub: "Pick one or more themes. We’ll show you the best places, hand-picked just for you, as if we were your local friend.",
    pickCta: "See results", pickHint: "Pick at least one",
    editInterests: "Interests",
    welcomeTitle: "Welcome to Bologna",
    welcomeSub: "Experiences picked for you, like a local friend would",
    homeTitle: "What would you like to do in Bologna?",
    filterAll: "All",
    experiencesTitle: "Experiences", experienceBadge: "Experience",
    freeByInvite: "Free · by invitation", free: "Free",
    docTitle: "Bologna doc", docSub: "The classics, with a local's tip",
    pickOrGuide: "Or", guideCta: "View the free guide",
    guideBannerKicker: "Free guide",
    guideBannerTitle: "The definitive guide to Bologna in {n} days",
    guideBannerStops: "stops · day by day",
    guideBannerCta: "See",
    recommendedBy: "Recommended by",
    itinFloatingCta: "Itinerary",
    guideTitle: "The definitive guide", guideSub: "Three days in Bologna, already planned.",
    guideBack: "Back to cards",
    guideGateTitle: "Unlock the guide", guideGateSub: "Leave your email, it's free.",
    guideUnlock: "Unlock",
    guideConsent: "I agree to the processing of my email, per the",
    installTitle: "Get Glocal on your phone!",
    installSub: "Just like a real app, in 10 seconds ✨",
    installIosStep1: "Tap here below", installIosStep1Detail: "the Share icon",
    installIosStep2: "Then tap", installIosStep2Detail: "\"Add to Home Screen\"",
    installAndroidStep1: "Tap the three dots", installAndroidStep1Detail: "at the top of the browser",
    installAndroidStep2: "Then tap", installAndroidStep2Detail: "\"Add to Home Screen\"",
    installCta: "Install now, one tap!", installGotIt: "Done, got it", installLater: "Later",
    madeTitle: "100% Made in Bo", madeSub: "Authentic experiences, born here",
    soundtrackTitle: "Bologna's soundtrack",
    soundtrackSub: "The playlist picked by locals, for your trip",
    soundtrackOpen: "Open in Spotify",
    betaBar: "🚧 Preview version",
    qfTitle: "How's it going?", qfSub: "Takes 10 seconds (optional)",
    qfRatingLabel: "How do you rate the experience so far?",
    qfImproveLabel: "What would you improve?", qfImprovePlaceholder: "Write here (optional)…",
    qfExtraLabel: "Is there anything you expected to find but didn't?", qfExtraPlaceholder: "Write here (optional)…",
    qfSkip: "Skip", qfSend: "Send", qfSending: "Sending…",
    qfThanks: "Thanks so much!",
    cookieText: "We use technical and analytics cookies to understand how the app is used and improve it.",
    cookieOk: "Got it", cookiePolicy: "Privacy",
    localTip: "The local's tip", localTipsBtn: "Local tips",
    localTipTitle: "Local tip",
    emptySection: "New content coming soon in this section.",
    book: "Book for free", bookDates: "See dates and book", addItinShort: "Itinerary", inItinShort: "Added",
    addItin: "Add to itinerary", inItin: "In itinerary",
    of: "of",
    tabHome: "Home", tabItin: "Itinerary",
    itinTitle: "Your itinerary",
    itinReminderOne: "place in your itinerary", itinReminderMany: "places in your itinerary",
    itinReminderCta: "View route",
    itinEmpty: "Add places and experiences from Home to build your itinerary.",
    remove: "Remove", clearAll: "Clear", goHome: "Go to Home",
    openInMaps: "Open in Google Maps", shareWa: "Share on WhatsApp",
    shareCardText: "Check out {title} on Glocal:",
    booking: "Book", name: "Full name", email: "Email",
    people: "People", date: "Date",
    bookingSubtitle: "Book in a few clicks. No payment, no commitment. You'll get a confirmation once your booking is finalized.",
    send: "Send request", sending: "Sending…",
    thanks: "Request sent", thanksSub: "Not a confirmation yet: the local will email you within 24 hours.",
    askLocalKicker: "Need help with your trip to Bologna?", askLocal: "Ask a local", askLocalMsg: "Hi Glocal! I'm in Bologna and I'd love a tip: ",
    whatsapp: "Message on WhatsApp", close: "Close", back: "Back", required: "Please fill in the required fields.",
    openNow: "Open now", closedNow: "Closed now", closesAt: "closes at", opensAt: "opens at",
    hoursTitle: "Hours", hoursSynced: "Hours synced from Google", open24h: "Open 24 hours",
    abandonedTitle: "Still deciding? 👀",
    abandonedBody: "You haven't booked an experience yet. Taking a look is free, no commitment.",
    abandonedCta: "See experiences",
    abandonedClose: "Keep browsing",
    socialProofWeek: "people booked this week",
    menuBtn: "See the menu", menuTitle: "Menu",
    bookOnline: "Book online", bookCall: "Call", bookWa: "WhatsApp",
    openNewTab: "Open in a new tab",
    embedHint: "Not loading? Open it in a new tab.",
    waBookText: "Hi! I found you on Glocal, I'd like to book at {title}.",
  },
};

/* --------------------------- PERSISTENZA ---------------------------------- */
const store = window.localStorage;
const load = (k, fb) => { try { const v = store.getItem(k); return v ? JSON.parse(v) : fb; } catch { return fb; } };
const save = (k, v) => { try { store.setItem(k, JSON.stringify(v)); } catch {} };
// flag "per questa sessione" (si azzera chiudendo l'app / la scheda)
const sessionFlag = (k) => { try { return window.sessionStorage.getItem(k) === "1"; } catch { return false; } };
const setSessionFlag = (k) => { try { window.sessionStorage.setItem(k, "1"); } catch {} };

const hasInterest = (p, id) => String(p.interests || "").split(",").map((s) => s.trim()).includes(id);
const isDoc = (p) => String(p.doc || "").trim().toLowerCase() === "yes";
const isMadeInBo = (p) => String(p.madeinbo || "").trim().toLowerCase() === "yes";
const splitList = (s) => String(s || "").split(",").map((x) => x.trim()).filter(Boolean);

/* --------------------------- MENU & PRENOTAZIONE --------------------------- */
// Colonne nuove nel foglio:
//  - menu_it / menu_en: menu nella lingua dell'utente (stesso formato di
//                 "menu"). Se c'è solo una lingua, si usa quella per tutti.
//  - menu:        uno o più URL separati da virgola. PDF su Cloudinary ->
//                 ogni pagina mostrata come immagine. Altri PDF -> visualizzatore
//                 Google nel popup. Link di Google Drive (condiviso con
//                 "chiunque abbia il link") -> anteprima Drive nel popup.
//                 Immagini (Cloudinary o
//                 .jpg/.png/.webp) -> mostrate impilate nel popup. Qualsiasi
//                 altro link (sito, PDF) -> aperto nel popup in un iframe, con
//                 "Apri in una nuova scheda" come riserva.
//  - booking:     metodi di prenotazione, separati da virgola, nell'ordine in
//                 cui compaiono i pulsanti: form | link | whatsapp | call.
//                 Vuoto + bookable = yes -> "form" (comportamento di prima).
//  - booking_url: pagina di prenotazione del gestionale (TheFork, Quandoo, …)
//                 usata dal metodo "link", aperta nel popup.
//  - phone:       numero per "call". Se vuoto usa "contact".
//  (- contact:    numero WhatsApp, già esistente, usato da "whatsapp".
//                 Se vuoto, "whatsapp" usa "phone": basta un numero solo.)
const IMG_RE = /\.(jpe?g|png|webp|gif|avif)(\?|#|$)/i;
const PDF_RE = /\.pdf(\?|#|$)/i;
const isPdfUrl = (u) => PDF_RE.test(u);
const isCloudinaryImage = (u) => /res\.cloudinary\.com\/.+\/image\/upload\//i.test(u);
// PDF caricato su Cloudinary come "image": Cloudinary sa trasformarne ogni
// pagina in un'immagine (pg_N), quindi lo mostriamo come menu a immagini.
const isCloudinaryPdf = (u) => isCloudinaryImage(u) && isPdfUrl(u);
const isImageUrl = (u) => IMG_RE.test(u) || (isCloudinaryImage(u) && !isPdfUrl(u));
// URL dell'immagine della pagina N di un PDF su Cloudinary.
const cloudinaryPdfPage = (u, n) => u.replace(/\/image\/upload\//i, `/image/upload/pg_${n},w_1400,f_auto,q_auto/`).replace(PDF_RE, ".jpg$1");
// Qualsiasi altro PDF (es. sul sito del locale): i telefoni non lo mostrano
// bene dentro un iframe, quindi passa dal visualizzatore di Google.
const pdfViewerUrl = (u) => `https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(u)}`;
// File su Google Drive (link di condivisione "chiunque abbia il link"):
// qualsiasi forma del link -> versione /preview, l'unica che Drive lascia
// incorporare, con tutte le pagine scorrevoli.
const driveFileId = (u) => (u.match(/drive\.google\.com\/(?:file\/d\/|open\?id=|uc\?(?:[^#]*&)?id=)([\w-]{10,})/) || [])[1];
const drivePreviewUrl = (u) => { const id = driveFileId(u); return id ? `https://drive.google.com/file/d/${id}/preview` : null; };
// URL da mettere nell'iframe per un menu che non è fatto di immagini.
const menuEmbedUrl = (u) => drivePreviewUrl(u) || (isPdfUrl(u) ? pdfViewerUrl(u) : u);

// Pagine di un PDF su Cloudinary: non sappiamo quante sono, quindi carica
// la pagina 1, e ogni volta che una pagina arriva prova la successiva;
// la prima che dà errore segna la fine del documento (max 30 pagine).
function PdfPages({ url, label }) {
  const [count, setCount] = useState(1);
  const [last, setLast] = useState(null); // numero dell'ultima pagina esistente
  const pages = Array.from({ length: last ?? count }, (_, i) => i + 1);
  return pages.map((n) => (
    <img key={n} src={cloudinaryPdfPage(url, n)} alt={`${label} ${n}`}
      onLoad={() => { if (last === null && n === count && count < 30) setCount(count + 1); }}
      onError={() => { if (last === null) setLast(n - 1); }}
      style={{ display: "block", width: "100%", height: "auto", borderBottom: `1px solid ${BRAND.border}` }} />
  ));
}

function bookingMethods(p) {
  const list = splitList(p.booking).map((m) => m.toLowerCase());
  const bookable = String(p.bookable || "").trim().toLowerCase() === "yes";
  // HORECA (bar, ristoranti, botteghe…): tutto passa da Glocal. Qualsiasi posto
  // prenotabile (bookable = yes o colonna "booking" compilata) mostra solo la
  // nostra richiesta di prenotazione; link esterni, WhatsApp e chiamata al
  // locale non si mostrano più: il locale lo contattiamo noi.
  // ESPERIENZE (madeinbo = yes): per ora restano come prima (link, WhatsApp,
  // chiamata o form, da colonna "booking"): date e posti li decide chi le
  // organizza, il loro flusso è ancora da definire.
  // TOUR / ESPERIENZE A DATE (booking = sessioni): date inserite dal partner
  // nel portale, prenotazione confermata subito (src/SessionBooking.js).
  if (list.includes("sessioni")) return ["sessioni"];
  if (!isMadeInBo(p) && (list.length || bookable)) return ["form"];
  const methods = list.length ? list : bookable ? ["form"] : [];
  return methods.filter((m) => {
    if (m === "form") return true;
    if (m === "link") return !!String(p.booking_url || "").trim();
    if (m === "whatsapp") return !!String(p.contact || p.phone || "").replace(/[^0-9]/g, "");
    if (m === "call") return !!String(p.phone || p.contact || "").replace(/[^0-9+]/g, "");
    return false;
  });
}

// Aggiunge i parametri UTM al link del gestionale, così anche il locale vede
// nelle sue statistiche che la prenotazione arriva da Glocal.
function withUtm(url) {
  try {
    const u = new URL(url);
    if (!u.searchParams.has("utm_source")) {
      u.searchParams.set("utm_source", "glocal");
      u.searchParams.set("utm_medium", "app");
      u.searchParams.set("utm_campaign", "booking");
    }
    return u.toString();
  } catch { return url; }
}

/* --------------------------- ORARI (Google sync) --------------------------- */
const IT_DAYS = ["domenica", "lunedì", "martedì", "mercoledì", "giovedì", "venerdì", "sabato"];

function parseOrariRows(orari) {
  if (!orari) return [];
  return String(orari).split("\n").map((r) => r.trim()).filter(Boolean);
}

function parseTimeRange(line) {
  const m = line.match(/(\d{1,2}):(\d{2})\s*[–-]\s*(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const toMin = (h, mm) => Number(h) * 60 + Number(mm);
  let start = toMin(m[1], m[2]);
  let end = toMin(m[3], m[4]);
  if (end <= start) end += 24 * 60; // fascia che attraversa la mezzanotte
  return { start, end };
}

function minToLabel(min) {
  const h = Math.floor(min / 60) % 24;
  const m = min % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

// Calcola aperto/chiuso ora leggendo la riga del giorno (e, se serve, quella
// di ieri per le fasce che sconfinano dopo mezzanotte). Best-effort: se il
// formato non combacia, torna null e si mostra solo l'elenco settimanale.
function getOpenStatus(orari) {
  const rows = parseOrariRows(orari);
  if (rows.length === 0) return null;

  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const todayName = IT_DAYS[now.getDay()];
  const yesterdayName = IT_DAYS[(now.getDay() + 6) % 7];

  const todayLine = rows.find((r) => r.toLowerCase().startsWith(todayName));
  const yesterdayLine = rows.find((r) => r.toLowerCase().startsWith(yesterdayName));

  // Caso "aperto 24 ore su 24": nessun range da calcolare, è sempre aperto.
  if (todayLine && todayLine.toLowerCase().includes("24 ore")) {
    return { open: true, allDay: true };
  }

  if (yesterdayLine) {
    const yRange = parseTimeRange(yesterdayLine);
    if (yRange && yRange.end > 24 * 60 && nowMin < yRange.end - 24 * 60) {
      return { open: true, closesAt: minToLabel(yRange.end - 24 * 60) };
    }
  }

  if (!todayLine) return null;
  const range = parseTimeRange(todayLine);
  if (!range) return { open: false }; // es. "chiuso"

  if (nowMin >= range.start && nowMin < range.end) return { open: true, closesAt: minToLabel(range.end % (24 * 60)) };
  if (nowMin < range.start) return { open: false, opensAt: minToLabel(range.start) };
  return { open: false };
}

/* --------------------------- LOGO ----------------------------------------- */
function Logo({ height = 26 }) {
  return <img src="/glocal-logo.png" alt="Glocal" style={{ height, width: "auto", display: "block" }} />;
}

/* --------------------------- BADGE ROTANTE --------------------------------- */
// Vaga per tutta la schermata di selezione interessi (posizione fixed, percorso
// ampio in loop) mentre cicla Nettuno / tortellino / Due Torri: ogni ~2.6s
// l'immagine cambia soggetto con una dissolvenza automatica (nessun effetto di
// rotazione/flip). Nessun badge/cerchio intorno: solo l'immagine già scontornata,
// con una leggera ombra per dare senso di "volo".
function RotatingBadge({ height = 108 }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const period = 2600; // deve combaciare con la durata dell'animazione glFlip
    const loop = setInterval(() => setIdx((i) => (i + 1) % ROTATOR_ITEMS.length), period);
    return () => clearInterval(loop);
  }, []);
  const item = ROTATOR_ITEMS[idx];
  return (
    <div className="gl-rotator-wrap" style={{ height }} aria-hidden="true">
      <div className="gl-rotator-float" style={{ height: "100%" }}>
        <img key={idx} src={item.src} alt="" className="gl-rotator-img" style={{ height: "100%", width: "auto", display: "block", filter: "drop-shadow(0 10px 16px rgba(20,16,10,0.28))" }} />
      </div>
    </div>
  );
}

/* --------------------------- ABANDONED CART -------------------------------- */
const INACTIVITY_MS = 45000;
const AWAY_MS = 20000;

function shouldShowAbandonedToday() {
  return load("gl_abandoned_seen_date", null) !== new Date().toDateString();
}
function markAbandonedSeenToday() { save("gl_abandoned_seen_date", new Date().toDateString()); }

// Trigger semplice: inattività 45s, oppure mouse che scappa verso l'alto
// (exit-intent desktop), oppure torna sulla tab dopo essere stato via 20s+
// (su mobile corrisponde a mettere l'app in background e tornare). Al
// massimo una volta al giorno, e solo se l'utente non ha ancora prenotato.
function useAbandonedCartTrigger({ enabled, onTrigger }) {
  const firedRef = useRef(false);
  const hiddenAtRef = useRef(null);

  useEffect(() => {
    if (!enabled || firedRef.current || !shouldShowAbandonedToday()) return;
    let idleTimer = null;
    const fire = () => {
      if (firedRef.current) return;
      firedRef.current = true;
      markAbandonedSeenToday();
      onTrigger();
    };
    const resetIdle = () => { if (idleTimer) clearTimeout(idleTimer); idleTimer = setTimeout(fire, INACTIVITY_MS); };
    const onMouseLeave = (e) => { if (e.clientY <= 0) fire(); };
    const onVisibility = () => {
      if (document.hidden) hiddenAtRef.current = Date.now();
      else if (hiddenAtRef.current && Date.now() - hiddenAtRef.current > AWAY_MS) fire();
    };

    resetIdle();
    const events = ["scroll", "touchstart", "click", "keydown"];
    events.forEach((ev) => window.addEventListener(ev, resetIdle, { passive: true }));
    window.addEventListener("mouseleave", onMouseLeave);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      if (idleTimer) clearTimeout(idleTimer);
      events.forEach((ev) => window.removeEventListener(ev, resetIdle));
      window.removeEventListener("mouseleave", onMouseLeave);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, onTrigger]);
}

function AbandonedCartModal({ t, onClose, onCta }) {
  return (
    <div onClick={onClose} className="gl-overlay" style={overlay}>
      <div onClick={(e) => e.stopPropagation()} className="gl-sheet" style={{ ...sheet, maxWidth: 440, padding: 26, textAlign: "center" }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>👀</div>
        <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 600, fontSize: 24, margin: "0 0 10px" }}>{t.abandonedTitle}</h3>
        <p style={{ fontSize: 15.5, lineHeight: 1.55, color: "#4a463d", margin: "0 0 22px" }}>{t.abandonedBody}</p>
        <button onClick={onCta} style={{ width: "100%", background: BRAND.green, color: "#fff", border: "none", borderRadius: 14, padding: 15, fontSize: 16, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", marginBottom: 10 }}>{t.abandonedCta}</button>
        <button onClick={onClose} style={{ width: "100%", background: "transparent", color: BRAND.muted, border: "none", padding: 8, fontSize: 14.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>{t.abandonedClose}</button>
      </div>
    </div>
  );
}

/* ------------------------------- APP -------------------------------------- */
export default function App() {
  // Lingua: se l'utente l'ha già scelta si rispetta quella; altrimenti si
  // parte dalla lingua del browser/telefono (italiano → IT, tutto il resto → EN),
  // così un turista straniero che scansiona il QR vede subito l'inglese.
  const [lang, setLang] = useState(() => load("gl_lang", detectLang()));
  const [tab, setTab] = useState("home");
  const [chosen, setChosen] = useState([]);          // interessi scelti (rivisti ogni apertura)
  // La app si apre SEMPRE sulla Home (cards cliccabili stile Airbnb Experience,
  // filtrabili con le chip "Tutti/Cibo/Bere/…"), anche quando si arriva dal
  // QR code. La schermata "scelta interessi" resta raggiungibile a mano
  // (icona ingranaggio in Home o tap sul logo), ma non è più la prima vista.
  const [picking, setPicking] = useState(false);     // true = schermata scelta interessi
  const [booking, setBooking] = useState(null);
  const [bookingFrom, setBookingFrom] = useState(null); // scheda da cui si è aperta la prenotazione: "indietro" ci riporta lì
  const [detail, setDetail] = useState(null);
  const [tipPlace, setTipPlace] = useState(null);
  const [itinerary, setItinerary] = useState(() => load("gl_itin", []));
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasBooked, setHasBooked] = useState(() => load("gl_has_booked", false));
  const [showGuide, setShowGuide] = useState(false);
  const [showAbandoned, setShowAbandoned] = useState(false);
  const [webSheet, setWebSheet] = useState(null); // popup menu / prenotazione esterna
  const [eventsOpen, setEventsOpen] = useState(null); // popup eventi: null = chiuso, altrimenti { focusId }
  const [tickerOpen, setTickerOpen] = useState(false); // card compatta eventi in basso (apertura automatica)
  const [eventsAutoShown, setEventsAutoShown] = useState(() => sessionFlag("gl_events_auto")); // già aperto da solo in questa sessione?
  const isPartner = isPartnerRoute();
  const events = useEvents(places);                  // eventi approvati (Supabase), [] se non configurato
  const upcoming = upcomingEvents(events);           // quelli dei prossimi 7 giorni, max 8 (mostre in corso in fondo)
  const t = T[lang];

  useEffect(() => { save("gl_lang", lang); document.documentElement.lang = lang; }, [lang]);
  useEffect(() => save("gl_itin", itinerary), [itinerary]);

  useEffect(() => {
    initGA();
    Papa.parse(CSV_URL, {
      download: true, header: true,
      // nomi colonna normalizzati: "Booking", " booking " ecc. valgono come "booking"
      transformHeader: (h) => String(h || "").trim().toLowerCase(),
      complete: (res) => {
        const rows = (res.data || []).filter((r) => r && r.id)
          .map((r) => ({ ...r, lat: Number(r.lat) || null, lng: Number(r.lng) || null }));
        setPlaces(rows); setLoading(false);
      },
      error: () => setLoading(false),
    });
  }, []);

  // Popup "carrello abbandonato": al massimo una volta al giorno, solo se
  // l'utente non ha ancora prenotato e non c'è già un altro pannello aperto.
  // È rimasto l'UNICO popup automatico dell'app (gli altri sono stati tolti
  // su richiesta: install hint, cookie banner, quick feedback, social proof).
  // Il popup eventi, invece, NON conta come "popup di disturbo": si apre da solo
  // una volta per sessione, un paio di secondi dopo l'apertura, se ci sono
  // eventi nei prossimi giorni. Nella sessione in cui si è aperto da solo il
  // carrello abbandonato non compare (mai due popup automatici insieme).
  useAbandonedCartTrigger({
    enabled: !isPartner && !picking && !hasBooked && !detail && !booking && !showAbandoned && !webSheet && !eventsOpen && !eventsAutoShown,
    onTrigger: () => setShowAbandoned(true),
  });

  const hasUpcoming = upcoming.length > 0;
  const busy = isPartner || picking || showGuide || !!detail || !!booking || !!webSheet || showAbandoned || !!tipPlace;
  useEffect(() => {
    if (!hasUpcoming || busy || eventsAutoShown || eventsOpen) return;
    const timer = setTimeout(() => {
      setSessionFlag("gl_events_auto");
      setEventsAutoShown(true);
      setTickerOpen(true); // card compatta in basso, non il popup grande
      track("show_events_ticker");
    }, 1800);
    return () => clearTimeout(timer);
  }, [hasUpcoming, busy, eventsAutoShown, eventsOpen]);
  // Link condiviso (…/?event=<id>): apre il popup direttamente su quell'evento
  // (al posto della card compatta) e ripulisce l'indirizzo.
  const deepEventDone = useRef(false);
  useEffect(() => {
    if (deepEventDone.current || isPartner) return;
    let id = null;
    try { id = new URLSearchParams(window.location.search).get("event"); } catch {}
    if (!id) { deepEventDone.current = true; return; }
    if (!events.length) return; // aspetta che arrivino gli eventi
    deepEventDone.current = true;
    try { window.history.replaceState(null, "", window.location.pathname); } catch {}
    const focusId = `${EVENT_PREFIX}${id}`;
    if (!events.some((e) => e.id === focusId)) return; // evento finito o ritirato: app normale
    setSessionFlag("gl_events_auto");
    setEventsAutoShown(true);
    setTickerOpen(false);
    setEventsOpen({ focusId });
    track("open_events", { from: "share_link" });
  }, [events, isPartner]);

  // Link condiviso da una card (…/?place=<id>): apre direttamente il dettaglio
  // di quel posto e ripulisce l'indirizzo. Id inesistente/ritirato: app normale.
  const deepPlaceDone = useRef(false);
  useEffect(() => {
    if (deepPlaceDone.current || isPartner) return;
    let id = null;
    try { id = new URLSearchParams(window.location.search).get("place"); } catch {}
    if (!id) { deepPlaceDone.current = true; return; }
    if (!places.length) return; // aspetta il foglio
    deepPlaceDone.current = true;
    try { window.history.replaceState(null, "", window.location.pathname); } catch {}
    const p = places.find((x) => String(x.id).trim() === String(id).trim());
    if (!p) return;
    setDetail(p);
    track("view_card", { card: p.title_it || p.id, section: p.interests, from: "share_link" });
  }, [places, isPartner]);

  const openEvents = (focusId = null, from = "home") => { track("open_events", { from }); setTickerOpen(false); setEventsOpen({ focusId }); };

  const toggleIn = (list, setList, id) => setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  const byId = (id) => (String(id).startsWith(EVENT_PREFIX) ? events.find((e) => e.id === id) : places.find((p) => p.id === id));
  const toggleChosen = (id) => setChosen((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  const markBooked = () => { save("gl_has_booked", true); setHasBooked(true); };

  // PORTALE LOCALI / ADMIN
  if (isPartner) {
    return (
      <Suspense fallback={<div style={{ minHeight: "100vh", background: BRAND.bg }} />}>
        <FontLink />
        <PartnerPortal places={places} />
      </Suspense>
    );
  }

  // bottone "Chiedi a un local": visibile anche col ticker eventi aperto
  // (il ticker si alza sopra il bottone invece di coprirlo)
  const askLocalVisible = !detail && !booking && !webSheet && !tipPlace && !eventsOpen && !showAbandoned;

  // popup eventi: la lista mostrata + l'evento da cui partire (se aperto dall'itinerario
  // e non più nei prossimi 7 giorni, lo aggiungiamo in testa)
  const popupEvents = eventsOpen?.focusId && !upcoming.some((e) => e.id === eventsOpen.focusId)
    ? [byId(eventsOpen.focusId), ...upcoming].filter(Boolean) : upcoming;
  const eventsPopup = eventsOpen && popupEvents.length > 0 && (
    <EventsPopup events={popupEvents} focusId={eventsOpen.focusId} lang={lang} itinerary={itinerary}
      onToggleItin={(id) => toggleIn(itinerary, setItinerary, id)}
      onOpenCard={(card) => { setEventsOpen(null); setDetail(card); }}
      onClose={() => setEventsOpen(null)} />
  );

  // schermata iniziale: scelta interessi (obbligatoria, rivista a ogni apertura)
  if (showGuide) {
    return (
      <div style={{ minHeight: "100vh", background: BRAND.bg, color: BRAND.ink, fontFamily: "'Archivo', system-ui, sans-serif" }}>
        <FontLink />
        <GuideTab t={t} lang={lang} places={places} onClose={() => setShowGuide(false)} onOpenDetail={setDetail} />
        {detail && <DetailModal place={detail} lang={lang} t={t} onClose={() => setDetail(null)} onBook={(p) => { setDetail(null); setBookingFrom(p); setBooking(p); }} onTip={(p) => setTipPlace(p)} onToggleItin={(id) => toggleIn(itinerary, setItinerary, id)} inItin={detail ? itinerary.includes(detail.id) : false} onOpenWeb={setWebSheet} onBookIntent={markBooked} />}
        {booking && <BookingSwitch place={booking} lang={lang} t={t} onClose={() => { setBooking(null); setBookingFrom(null); }} onBack={() => { const from = bookingFrom; setBooking(null); setBookingFrom(null); if (from) setDetail(from); }} onBooked={markBooked} />}
        {tipPlace && <LocalTipSheet place={tipPlace} tip={tipPlace[`tip_${lang}`]} lang={lang} t={t} onClose={() => setTipPlace(null)} />}
        {webSheet && <WebSheet sheetData={webSheet} t={t} onClose={() => setWebSheet(null)} />}
      </div>
    );
  }

  if (picking) {
    return (
      <div style={{ minHeight: "100vh", background: BRAND.bg, color: BRAND.ink, fontFamily: "'Archivo', system-ui, sans-serif" }}>
        <FontLink />
        <header style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", padding: "14px 18px", borderBottom: `1px solid ${BRAND.border}` }}>
          <span />
          <div style={{ justifySelf: "center" }}><Logo /></div>
          <div style={{ justifySelf: "end" }}><LangToggle lang={lang} setLang={setLang} /></div>
        </header>
        <InterestPicker
          t={t} lang={lang} chosen={chosen} onToggle={toggleChosen}
          onDone={() => {
            track("select_interests", { interests: chosen.join(",") });
            setTab("home");
            setPicking(false);
          }}
        />
        <RotatingBadge />
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: BRAND.bg, color: BRAND.ink, fontFamily: "'Archivo', system-ui, sans-serif" }}>
      <FontLink />
      <header style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", padding: "14px 18px", position: "sticky", top: 0, background: "rgba(251,248,240,0.92)", backdropFilter: "blur(10px)", zIndex: 30, borderBottom: `1px solid ${BRAND.border}` }}>
        <span style={{ justifySelf: "start", display: "flex", alignItems: "center", gap: 14 }}>
          <DesktopNav t={t} tab={tab} setTab={setTab} itinCount={itinerary.length} />
          {loading && <Spinner />}
        </span>
        <div style={{ justifySelf: "center" }}>
          <button onClick={() => { track("logo_go_home"); setTab("home"); }} aria-label={t.homeTitle} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "block" }}>
            <Logo />
          </button>
        </div>
        <div style={{ justifySelf: "end" }}><LangToggle lang={lang} setLang={setLang} /></div>
      </header>

      <main className="gl-main" style={{ maxWidth: 720, margin: "0 auto", padding: "0 0 96px" }}>
        {tab === "home" && (
          <HomeTab t={t} lang={lang} loading={loading} places={places} chosen={chosen}
            onEditInterests={() => setPicking(true)}
            onBook={setBooking} onDetail={setDetail} onTip={setTipPlace}
            itinerary={itinerary} onToggleItin={(id) => toggleIn(itinerary, setItinerary, id)}
            onOpenItin={() => setTab("itin")}
            onOpenGuide={() => setShowGuide(true)}
            upcomingEvents={upcoming} onOpenEvents={() => openEvents(null, "home")}
            hideItinPill={tickerOpen} />
        )}
        {tab === "itin" && (
          <ItineraryTab t={t} lang={lang} items={itinerary.map(byId).filter(Boolean)}
            onRemove={(id) => toggleIn(itinerary, setItinerary, id)} onClear={() => setItinerary([])} onGoHome={() => setTab("home")}
            onOpenDetail={(p) => (p.isEvent ? openEvents(p.id, "itinerary") : setDetail(p))} />
        )}
      </main>

      <TabBar t={t} tab={tab} setTab={setTab} itinCount={itinerary.length} />

      {detail && <DetailModal place={detail} lang={lang} t={t} onClose={() => setDetail(null)} onBook={(p) => { setDetail(null); setBookingFrom(p); setBooking(p); }} onTip={(p) => setTipPlace(p)} onToggleItin={(id) => toggleIn(itinerary, setItinerary, id)} inItin={detail ? itinerary.includes(detail.id) : false} onOpenWeb={setWebSheet} onBookIntent={markBooked} />}
      {booking && <BookingSwitch place={booking} lang={lang} t={t} onClose={() => { setBooking(null); setBookingFrom(null); }} onBack={() => { const from = bookingFrom; setBooking(null); setBookingFrom(null); if (from) setDetail(from); }} onBooked={markBooked} />}
      {tipPlace && <LocalTipSheet place={tipPlace} tip={tipPlace[`tip_${lang}`]} lang={lang} t={t} onClose={() => setTipPlace(null)} />}
        {webSheet && <WebSheet sheetData={webSheet} t={t} onClose={() => setWebSheet(null)} />}
      {eventsPopup}
      {tickerOpen && tab === "home" && !eventsOpen && !detail && !booking && !webSheet && upcoming.length > 0 && (
        <EventsTicker events={upcoming} lang={lang} itinerary={itinerary}
          onToggleItin={(id) => toggleIn(itinerary, setItinerary, id)}
          onOpen={(id) => openEvents(id, "ticker")}
          onClose={() => setTickerOpen(false)}
          lifted={askLocalVisible} />
      )}
      {askLocalVisible && <AskLocalButton t={t} />}
      {showAbandoned && (
        <AbandonedCartModal t={t} onClose={() => setShowAbandoned(false)}
          onCta={() => { setShowAbandoned(false); setTab("home"); }} />
      )}
    </div>
  );
}

/* --------------------------- INTEREST PICKER ------------------------------ */
function InterestPicker({ t, lang, chosen, onToggle, onDone }) {
  return (
    <main style={{ maxWidth: 560, margin: "0 auto", padding: "0 22px", minHeight: "calc(100vh - 60px)", display: "flex", flexDirection: "column" }}>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", paddingTop: 44, paddingBottom: 28 }}>
        <h1 style={{ fontFamily: "'Fraunces', serif", fontWeight: 600, fontSize: "clamp(30px, 7vw, 42px)", letterSpacing: "-0.02em", margin: "0 0 10px", lineHeight: 1.08 }}>{t.pickTitle}</h1>
        <p style={{ color: BRAND.muted, margin: "0 0 30px", fontSize: 16, lineHeight: 1.5 }}>{t.pickSub}</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 12, alignContent: "start" }}>
          {SECTIONS.map((o, i) => {
            const active = chosen.includes(o.id);
            return (
              <button key={o.id} onClick={() => onToggle(o.id)} className="gl-pick-card" style={{ animationDelay: `${i * 60}ms`, position: "relative", display: "flex", alignItems: "center", gap: 11, padding: "18px 18px", borderRadius: 16, cursor: "pointer", background: active ? BRAND.green : BRAND.card, color: active ? "#fff" : BRAND.ink, border: `1.5px solid ${active ? BRAND.green : BRAND.border}`, fontSize: 15.5, fontWeight: 500, fontFamily: "inherit", textAlign: "left", boxShadow: active ? "0 10px 24px rgba(196,120,60,0.28)" : "none", transition: "background .15s, box-shadow .15s" }}>
                <span style={{ width: 26, height: 26, flexShrink: 0, display: "inline-flex" }}><img src={o.icon} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} /></span><span>{o[lang]}</span>
                {active && <span className="gl-check-pop" style={{ position: "absolute", top: 8, right: 8, width: 22, height: 22, borderRadius: "50%", background: "#fff", color: BRAND.green, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 900, boxShadow: "0 2px 6px rgba(0,0,0,0.18)" }}>✓</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* area sticky in basso: pulsante "vedi risultati" (azione primaria) — il pulsante guida è stato tolto */}
      <div style={{ position: "sticky", bottom: 0, background: BRAND.bg, paddingBottom: "calc(20px + env(safe-area-inset-bottom, 0))", paddingTop: 12 }}>
        <button onClick={onDone} disabled={chosen.length === 0} style={{ width: "100%", background: chosen.length ? BRAND.green : "#d9d3c4", color: "#fff", border: "none", borderRadius: 16, padding: 17, fontSize: 17, fontWeight: 700, cursor: chosen.length ? "pointer" : "default", fontFamily: "inherit", transition: "background .15s" }}>
          {chosen.length ? t.pickCta : t.pickHint}
        </button>
      </div>
    </main>
  );
}

/* --------------------------- GUIDA 3 GIORNI -------------------------------- */
const GUIDE_DAYS = [1, 2, 3];

function GuideTab({ t, lang, places, onClose, onOpenDetail }) {
  const [day, setDay] = useState(1);
  const [unlocked, setUnlocked] = useState(() => load("gl_guide_unlocked", false));
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(false);

  const byGuideDay = (n) =>
    places
      .filter((p) => String(p.guida_giorno) === String(n))
      .sort((a, b) => Number(a.guida_ordine || 0) - Number(b.guida_ordine || 0));

  const stops = byGuideDay(day);

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!email.includes("@") || !consent) { setErr(true); return; }
    setErr(false); setSending(true);
    try {
      const res = await fetch("/.netlify/functions/subscribe-guide", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, lang }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        console.error("subscribe-guide failed", res.status, body);
        setErr(true);
        return;
      }
      save("gl_guide_unlocked", true);
      setUnlocked(true);
      track("unlock_guide", { email_domain: email.split("@")[1] || "" });
    } catch (err) {
      console.error("subscribe-guide network error", err);
      setErr(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <main style={{ maxWidth: 720, margin: "0 auto", padding: "18px 18px 60px" }}>
      <button onClick={onClose} style={{ background: "none", border: "none", color: BRAND.muted, fontSize: 13.5, fontWeight: 600, cursor: "pointer", padding: 0, marginBottom: 18 }}>
        &larr; {t.guideBack}
      </button>

      <h1 style={{ fontFamily: "'Fraunces', serif", fontWeight: 600, fontSize: "clamp(30px, 7vw, 42px)", letterSpacing: "-0.02em", margin: "0 0 6px", lineHeight: 1.05 }}>
        {t.guideTitle}
      </h1>
      <p style={{ color: BRAND.muted, margin: "0 0 24px", fontSize: 15 }}>{t.guideSub}</p>

      <div style={{ display: "flex", gap: 20, borderBottom: `1px solid ${BRAND.border}`, marginBottom: 4 }}>
        {GUIDE_DAYS.map((n) => (
          <button key={n} onClick={() => setDay(n)} style={{ background: "none", border: "none", padding: "10px 0", fontSize: 13, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", cursor: "pointer", borderBottom: `3px solid ${day === n ? BRAND.green : "transparent"}`, color: day === n ? BRAND.ink : BRAND.muted }}>
            {lang === "en" ? `Day 0${n}` : `Giorno 0${n}`}
          </button>
        ))}
      </div>

      <div style={{ position: "relative" }}>
        <div style={{ filter: unlocked ? "none" : "blur(7px)", pointerEvents: unlocked ? "auto" : "none", userSelect: unlocked ? "auto" : "none" }}>
          {stops.map((stop) => {
            const name = lang === "en" ? stop.title_en || stop.title_it : stop.title_it;
            const note = lang === "en" ? stop.guida_nota_en || stop.guida_nota_it : stop.guida_nota_it;
            // L'intera riga apre la scheda completa (foto, descrizione, prezzo,
            // prenota, aggiungi a itinerario) — stessa esperienza delle card
            // ovunque nell'app, invece del vecchio accordion che si apriva sul posto.
            return (
              <div key={stop.id} style={{ borderBottom: `1px solid ${BRAND.border}` }}>
                <button onClick={() => { track("view_card", { card: stop.title_it || stop.id, from: "guide" }); onOpenDetail(stop); }} style={{ width: "100%", display: "flex", gap: 14, padding: "18px 0", background: "none", border: "none", textAlign: "left", cursor: "pointer", fontFamily: "inherit" }}>
                  {stop.guida_ora && (
                    <div style={{ width: 46, flexShrink: 0, fontFamily: "'Fraunces', serif", fontWeight: 600, fontSize: 15, color: BRAND.green, paddingTop: 2 }}>
                      {stop.guida_ora}
                    </div>
                  )}
                  <div style={{ width: 64, height: 64, flexShrink: 0, borderRadius: 12, background: `${BRAND.border}`, backgroundImage: stop.image ? `url(${stop.image})` : undefined, backgroundSize: "cover", backgroundPosition: "center" }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                      <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 600, fontSize: 17, color: BRAND.ink }}>{name}</div>
                      {stop.price && <div style={{ flexShrink: 0, fontSize: 13.5, fontWeight: 700, color: BRAND.red, paddingTop: 2 }}>{displayPrice(stop, lang)}</div>}
                    </div>
                    {note && <p style={{ margin: "4px 0 0", fontSize: 13, color: BRAND.muted, lineHeight: 1.4 }}>{note}</p>}
                  </div>
                </button>
              </div>
            );
          })}
        </div>

        {!unlocked && (
          // alignItems: "flex-start" + paddingTop, invece di "center": così il
          // popup email sta vicino alla cima (appena sotto i tab giorno) invece
          // di centrarsi in mezzo a tutto l'elenco sfocato, che può essere lungo.
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "20px 20px 20px" }}>
            <form onSubmit={submitEmail} style={{ marginTop: 28, background: BRAND.card, border: `1px solid ${BRAND.border}`, borderRadius: 18, padding: 24, maxWidth: 340, width: "100%", boxShadow: "0 12px 30px rgba(40,30,15,0.18)", textAlign: "center" }}>
              <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 600, fontSize: 20, marginBottom: 6 }}>{t.guideGateTitle}</div>
              <p style={{ fontSize: 13, color: BRAND.muted, margin: "0 0 16px" }}>{t.guideGateSub}</p>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t.email}
                style={{ width: "100%", boxSizing: "border-box", padding: "12px 14px", borderRadius: 12, border: `1.5px solid ${err ? BRAND.red : BRAND.border}`, fontSize: 14.5, fontFamily: "inherit", marginBottom: 10 }} />
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, textAlign: "left", fontSize: 12, color: BRAND.muted, lineHeight: 1.4, marginBottom: 12, cursor: "pointer" }}>
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} style={{ marginTop: 2, flexShrink: 0 }} />
                <span>
                  {t.guideConsent}{" "}
                  <a href="https://www.iubenda.com/privacy-policy/67582598" target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: BRAND.ink, textDecoration: "underline" }}>{t.cookiePolicy}</a>
                </span>
              </label>
              <button type="submit" disabled={sending || !consent} style={{ width: "100%", background: consent ? BRAND.green : "#d9d3c4", color: "#fff", border: "none", borderRadius: 12, padding: 13, fontSize: 14.5, fontWeight: 700, cursor: consent ? "pointer" : "default", fontFamily: "inherit" }}>
                {sending ? t.sending : t.guideUnlock}
              </button>
              {err && <div style={{ color: BRAND.red, fontSize: 12, marginTop: 8 }}>{t.required}</div>}
            </form>
          </div>
        )}
      </div>
    </main>
  );
}

/* ------------------------------ HOME TAB ---------------------------------- */
// Prima schermata dell'app (anche da QR code): titolo + chip filtro
// "Tutti/Cibo/Bere/…", poi Esperienze (cards stile Airbnb Experience,
// cliccabili), banner guida gratuita, e a seguire le sezioni per interesse /
// Bologna doc / colonna sonora, come prima. Il filtro chip è indipendente
// dagli "interessi" salvati (chosen): è solo per navigare la Home.
function HomeTab({ t, lang, loading, places, chosen, onEditInterests, onBook, onDetail, onTip, itinerary, onToggleItin, onOpenItin, onOpenGuide, upcomingEvents = [], onOpenEvents, hideItinPill }) {
  const [filter, setFilter] = useState("all"); // "all" oppure un id di SECTIONS
  if (loading) return <div style={{ padding: "22px 18px" }}><DeckSkeleton /></div>;
  // Interessi scelti nella schermata "Cosa ti interessa": se ce ne sono,
  // la Home mostra SOLO quelle sezioni (e solo quelle chip). "Tutti" = tutti
  // gli interessi scelti. Nessun interesse scelto = si vede tutto.
  const scope = chosen.length ? SECTIONS.filter((s) => chosen.includes(s.id)) : SECTIONS;
  const visibleSections = filter === "all" ? scope : scope.filter((s) => s.id === filter);
  const docs = places.filter(isDoc); // TUTTI i classici, sempre, a prescindere dagli interessi
  // Esperienze: se ci sono interessi scelti, solo quelle che ne hanno almeno
  // uno (quelle senza interessi compilati nel foglio restano sempre visibili).
  const made = places.filter(isMadeInBo).filter((p) =>
    !chosen.length || !String(p.interests || "").trim() || chosen.some((id) => hasInterest(p, id)));
  const guideStopsCount = places.filter((p) => p.guida_giorno).length;

  return (
    <div className="gl-home" style={{ padding: "8px 18px 0" }}>
      {/* COSA TI VA DI FARE A BOLOGNA — titolo + sottotitolo Home, prima cosa che si vede aprendo l'app */}
      <h1 className="gl-home-title" style={{ fontFamily: "'Fraunces', serif", fontWeight: 600, fontSize: "clamp(28px, 7.5vw, 36px)", lineHeight: 1.08, letterSpacing: "-0.02em", margin: "14px 0 6px" }}>
        {t.homeTitle}
      </h1>
      <p style={{ fontSize: 13.5, color: BRAND.muted, margin: "0 0 16px", lineHeight: 1.4 }}>{t.welcomeSub}</p>
      <FilterChips t={t} lang={lang} sections={scope} filter={filter} setFilter={setFilter} />

      {/* barra: modifica interessi — piccola, sotto le chip */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginTop: 14 }}>
        <button onClick={onEditInterests} style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "transparent", color: BRAND.ink, border: `1.5px solid ${BRAND.border}`, borderRadius: 999, padding: "8px 15px", fontSize: 13.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
          <span>⚙︎</span>{t.editInterests}
          {chosen.length > 0 && <span style={{ minWidth: 18, height: 18, borderRadius: 9, background: BRAND.green, color: "#fff", fontSize: 11, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "0 4px" }}>{chosen.length}</span>}
        </button>
      </div>

      {/* ESPERIENZE — cards Airbnb-style (badge + foto + titolo + prezzo), sempre in cima, cliccabili come le altre.
          alignItems: "flex-start" evita che una card con titolo su 2 righe risulti più in alto/bassa delle altre. */}
      {made.length > 0 && (
        <section style={{ marginTop: 22 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: BRAND.muted, marginBottom: 10 }}>{t.experiencesTitle}</div>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12, overflowX: "auto", paddingBottom: 4, marginInline: -18, paddingInline: 18 }} className="gl-exp-row">
            {made.map((p) => (
              <PlaceCard key={p.id} place={p} lang={lang} t={t} badge={t.experienceBadge}
                onClick={() => { track("view_card", { card: p.title_it || p.id, section: "experiences" }); onDetail(p); }}
                inItin={itinerary.includes(p.id)} onToggleItin={() => onToggleItin(p.id)} />
            ))}
          </div>
        </section>
      )}

      {/* EVENTI IN CITTÀ — striscia scura, riapre il popup eventi (solo se ci sono eventi nei prossimi giorni) */}
      {/* su desktop eventi + guida stanno affiancati (gl-promo-row), su mobile impilati come prima */}
      <div className={upcomingEvents.length > 0 ? "gl-promo-row" : undefined}>
        {upcomingEvents.length > 0 && <EventsStrip events={upcomingEvents} lang={lang} onOpen={onOpenEvents} />}

        {/* GUIDA GRATUITA — banner rosso, porta alla guida "3 giorni a Bologna" */}
        <GuideBanner t={t} stopsCount={guideStopsCount} onOpen={onOpenGuide} />
      </div>

      {itinerary.length > 0 && (
        <button onClick={onOpenItin} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, width: "100%", marginTop: 22, background: "rgba(56,176,74,0.10)", border: `1.5px solid ${BRAND.green}`, borderRadius: 14, padding: "12px 16px", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 700, color: BRAND.greenDark }}>
            🗺️ {itinerary.length} {itinerary.length === 1 ? t.itinReminderOne : t.itinReminderMany}
          </span>
          <span style={{ fontSize: 13, fontWeight: 700, color: BRAND.greenDark, whiteSpace: "nowrap" }}>{t.itinReminderCta} ›</span>
        </button>
      )}

      {/* SEZIONI per interesse (filtrate dalla chip selezionata) — solo contenuti NON doc */}
      {visibleSections.map((sec) => {
        const normal = places.filter((p) => hasInterest(p, sec.id) && !isDoc(p));
        if (normal.length === 0) return null;
        return (
          <section key={sec.id} style={{ marginTop: 32 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <span style={{ width: 24, height: 24, flexShrink: 0, display: "inline-flex" }}><img src={sec.icon} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} /></span>
              <h2 style={{ fontFamily: "'Fraunces', serif", fontWeight: 600, fontSize: "clamp(24px, 5vw, 30px)", margin: 0, letterSpacing: "-0.02em" }}>{sec[lang]}</h2>
            </div>
            <Deck items={normal} lang={lang} t={t} onBook={onBook} onDetail={onDetail} onTip={onTip}
              itinerary={itinerary} onToggleItin={onToggleItin} />
          </section>
        );
      })}

      {/* BOLOGNA DOC — sezione fissa, sempre visibile, uguale per tutti */}
      {docs.length > 0 && (
        <section style={{ marginTop: 36 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <span style={{ width: 24, height: 24, flexShrink: 0, display: "inline-flex" }}><img src="/icons/star.png" alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} /></span>
            <h2 style={{ fontFamily: "'Fraunces', serif", fontWeight: 600, fontSize: "clamp(24px, 5vw, 30px)", margin: 0, letterSpacing: "-0.02em" }}>{t.docTitle}</h2>
          </div>
          <p style={{ fontSize: 13.5, color: BRAND.muted, margin: "4px 0 12px" }}>{t.docSub}</p>
          <Deck items={docs} lang={lang} t={t} onBook={onBook} onDetail={onDetail} onTip={onTip}
            itinerary={itinerary} onToggleItin={onToggleItin} isDocDeck />
        </section>
      )}

      {/* LA COLONNA SONORA DI BOLOGNA — sezione fissa, sempre visibile, in fondo */}
      <section style={{ marginTop: 36 }}>
        <SoundtrackCard t={t} />
      </section>

      {/* INSTAGRAM — link minimale, in fondo alla pagina */}
      <div style={{ display: "flex", justifyContent: "center", marginTop: 28 }}>
        <a href="https://www.instagram.com/g.localapp/" target="_blank" rel="noreferrer" aria-label="Instagram"
          onClick={() => track("open_instagram")}
          style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 40, height: 40, borderRadius: "50%", border: `1.5px solid ${BRAND.border}`, color: BRAND.muted, transition: "all .15s" }}>
          <InstagramIcon />
        </a>
      </div>

      <div style={{ height: 20 }} />

      {/* pillola flottante "N · Itinerario" — visibile ovunque in Home quando c'è già qualcosa nell'itinerario */}
      {itinerary.length > 0 && !hideItinPill && <ItinFloatingButton t={t} count={itinerary.length} onClick={onOpenItin} />}
    </div>
  );
}

/* --------------------------- FILTER CHIPS ---------------------------------- */
// Barra chip "Tutti / Cibo / Bere / Natura / …" sotto il titolo Home, con le
// stesse icone della schermata "scelta interessi". Selezione singola (a
// differenza della vecchia scelta interessi, multi-select): filtra solo le
// sezioni per interesse più sotto nella pagina, non Esperienze/Doc.
function FilterChips({ t, lang, sections, filter, setFilter }) {
  const list = sections || SECTIONS;
  // con un solo interesse scelto la chip "Tutti" sarebbe un doppione: si mostra solo quella
  const chips = [...(list.length > 1 ? [{ id: "all", label: t.filterAll, icon: null }] : []), ...list.map((s) => ({ id: s.id, label: s[lang], icon: s.icon }))];
  return (
    <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 2, marginInline: -18, paddingInline: 18 }} className="gl-chip-row">
      {chips.map((c) => {
        const active = filter === c.id || list.length === 1;
        return (
          <button key={c.id} onClick={() => setFilter(c.id)} style={{ flexShrink: 0, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 7, background: active ? "rgba(56,176,74,0.12)" : "transparent", color: active ? BRAND.greenDark : BRAND.ink, border: `1.5px solid ${active ? BRAND.green : BRAND.border}`, borderRadius: 999, padding: "9px 17px 9px 14px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            {c.icon && <span style={{ width: 18, height: 18, flexShrink: 0, display: "inline-flex" }}><img src={c.icon} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} /></span>}
            {c.label}
          </button>
        );
      })}
    </div>
  );
}

/* --------------------------- PLACE CARD ------------------------------------ */
// Card unica per Esperienze + tutte le sezioni/Bologna doc: sempre in stile
// Airbnb Experience, piccola (badge opzionale, foto, titolo, sottotitolo) e
// cliccabile — apre il dettaglio, dove restano prenotazione/tip/itinerario.
// "alignItems: flex-start" sul contenitore riga evita che una card più alta
// delle altre (per titolo su 2 righe) spinga in giù/su le vicine.
function PlaceCard({ place, lang, t, badge, onClick, inItin, onToggleItin }) {
  const title = place[`title_${lang}`];
  const subtitle = displayPrice(place, lang) || place.location || "";
  return (
    <button onClick={onClick} className="gl-place-card" style={{ flexShrink: 0, width: "44%", minWidth: 154, maxWidth: 200, background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left", fontFamily: "inherit", display: "flex", flexDirection: "column" }}>
      <div className="gl-place-img" style={{ position: "relative", aspectRatio: "4/3", borderRadius: 18, overflow: "hidden", background: "#eee" }}>
        {place.image
          ? <img src={place.image} alt={title} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} loading="lazy" />
          : <div style={{ width: "100%", height: "100%", background: "#ece4d6" }} />}
        {badge && <span style={{ position: "absolute", top: 10, left: 10, background: "rgba(20,16,10,0.72)", color: "#fff", fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", padding: "5px 10px", borderRadius: 999 }}>{badge}</span>}
        {onToggleItin && (
          <span
            role="button"
            aria-label={inItin ? t.inItinShort : t.addItinShort}
            onClick={(e) => { e.stopPropagation(); if (!inItin) track("add_to_itinerary", { card: place.title_it || place.id }); onToggleItin(); }}
            style={{ position: "absolute", top: 10, right: 10, width: 32, height: 32, borderRadius: "50%", background: inItin ? BRAND.greenDark : "rgba(255,255,255,0.92)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.2)", cursor: "pointer" }}>
            <PinIcon size={17} filled={inItin} color={inItin ? "#fff" : BRAND.ink} />
          </span>
        )}
      </div>
      <div translate="no" className="notranslate" style={{ fontWeight: 700, fontSize: 14.5, lineHeight: 1.3, marginTop: 8 }}>{title}</div>
      {subtitle && <div style={{ fontSize: 12.5, color: BRAND.muted, marginTop: 2 }}>{subtitle}</div>}
    </button>
  );
}

/* --------------------------- GUIDE BANNER ----------------------------------- */
// Banner rosso "Guida gratuita" — porta alla GuideTab (3 giorni a Bologna).
function GuideBanner({ t, stopsCount, onOpen }) {
  const title = t.guideBannerTitle.replace("{n}", GUIDE_DAYS.length);
  return (
    <button onClick={() => { track("open_guide_banner"); onOpen(); }} style={{ display: "block", width: "100%", textAlign: "left", marginTop: 22, background: BRAND.red, color: "#fff", border: "none", borderRadius: 20, padding: "22px 22px 24px", cursor: "pointer", fontFamily: "inherit" }}>
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", opacity: 0.85, marginBottom: 8 }}>{t.guideBannerKicker}</div>
      <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 600, fontSize: "clamp(22px, 6vw, 28px)", lineHeight: 1.1, letterSpacing: "-0.01em", marginBottom: 16 }}>{title}</div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <span style={{ fontSize: 13, opacity: 0.9 }}>{stopsCount > 0 ? `${stopsCount} ${t.guideBannerStops}` : t.guideBannerStops}</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#fff", color: BRAND.red, borderRadius: 999, padding: "9px 16px", fontSize: 13.5, fontWeight: 700, flexShrink: 0 }}>{t.guideBannerCta} →</span>
      </div>
    </button>
  );
}

/* --------------------------- FLOATING ITINERARY BUTTON ---------------------- */
// Pillola flottante in basso a destra (sopra la TabBar) con il conteggio
// dell'itinerario — scorciatoia rapida, visibile mentre si scorre la Home.
function ItinFloatingButton({ t, count, onClick }) {
  return (
    <button onClick={onClick} className="gl-itin-fab" style={{ position: "fixed", right: 16, bottom: "calc(74px + env(safe-area-inset-bottom, 0))", zIndex: 35, display: "inline-flex", alignItems: "center", gap: 8, background: BRAND.green, color: "#fff", border: "none", borderRadius: 999, padding: "12px 18px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 8px 22px rgba(56,176,74,0.4)" }}>
      <span style={{ fontSize: 16 }}>🗺️</span>{count} · {t.itinFloatingCta}
    </button>
  );
}

/* --------------------------- CHIEDI A UN LOCAL (WhatsApp) ------------------ */
// Bottone fisso in basso a sinistra (l'itinerario sta a destra): apre una chat
// WhatsApp con il numero Glocal e un messaggio già scritto. Risponde una persona.
const ASK_LOCAL_WA = "393200849247";
function AskLocalButton({ t }) {
  const href = `https://wa.me/${ASK_LOCAL_WA}?text=${encodeURIComponent(t.askLocalMsg)}`;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="gl-ask-fab"
      onClick={() => track("open_whatsapp", { from: "ask_local" })}
      aria-label={`${t.askLocalKicker} ${t.askLocal} — WhatsApp`}
      style={{ position: "fixed", left: 16, bottom: "calc(74px + env(safe-area-inset-bottom, 0))", zIndex: 35, display: "inline-flex", alignItems: "center", gap: 8, background: "#25D366", color: "#fff", textDecoration: "none", borderRadius: 999, maxWidth: "min(250px, calc(100vw - 180px))", padding: "9px 16px 9px 12px", fontSize: 14, fontWeight: 700, lineHeight: 1.2, fontFamily: "inherit", boxShadow: "0 8px 22px rgba(37,211,102,0.4)" }}>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="#fff" aria-hidden="true" style={{ flexShrink: 0 }}><path d="M17.47 14.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.65.07-.3-.15-1.25-.46-2.38-1.47-.88-.79-1.47-1.76-1.65-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.08 1.75-.72 2-1.41.25-.69.25-1.29.17-1.41-.07-.12-.27-.2-.57-.35zM12.05 21.5h-.01a9.4 9.4 0 0 1-4.8-1.31l-.34-.2-3.57.94.95-3.48-.22-.36a9.43 9.43 0 0 1-1.45-5.03c0-5.21 4.24-9.45 9.46-9.45 2.52 0 4.9.99 6.68 2.77a9.4 9.4 0 0 1 2.77 6.69c0 5.21-4.24 9.45-9.47 9.45zm8.04-17.5A11.3 11.3 0 0 0 12.05.67C5.78.67.68 5.77.68 12.04c0 2 .52 3.96 1.52 5.69L.58 23.33l5.73-1.5a11.33 11.33 0 0 0 5.73 1.46h.01c6.27 0 11.37-5.1 11.37-11.37 0-3.04-1.18-5.89-3.33-8.04z"/></svg>
      <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <span style={{ fontSize: 11.5, fontWeight: 500, opacity: 0.92 }}>{t.askLocalKicker}</span>
        <span>{t.askLocal}</span>
      </span>
    </a>
  );
}

/* --------------------------- PIN ICON (itinerario) ------------------------- */
// Pin mappa: vuoto = non nell'itinerario, pieno = aggiunto.
function PinIcon({ size = 18, filled = false, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: "block", flexShrink: 0 }}>
      <path d="M12 21.5s-7-6.1-7-11.5a7 7 0 0 1 14 0c0 5.4-7 11.5-7 11.5Z" fill={filled ? color : "none"} />
      <circle cx="12" cy="10" r="2.6" fill={filled ? (color === "#fff" ? BRAND.greenDark : "#fff") : "none"} stroke={filled ? "none" : color} />
    </svg>
  );
}

/* --------------------------- INSTAGRAM ICON -------------------------------- */
function InstagramIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.2" cy="6.8" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

/* --------------------------- SOUNDTRACK CARD ------------------------------- */
function SoundtrackCard({ t }) {
  return (
    <div style={{ background: BRAND.card, borderRadius: 22, overflow: "hidden", border: `1px solid ${BRAND.border}`, boxShadow: "0 6px 22px rgba(40,30,15,0.08)" }}>
      <div style={{ padding: "18px 18px 4px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <span style={{ fontSize: 22 }}>🎧</span>
          <h2 style={{ fontFamily: "'Fraunces', serif", fontWeight: 600, fontSize: "clamp(22px, 5vw, 26px)", margin: 0, letterSpacing: "-0.02em" }}>{t.soundtrackTitle}</h2>
        </div>
        <p style={{ fontSize: 13.5, color: BRAND.muted, margin: "4px 0 14px" }}>{t.soundtrackSub}</p>
      </div>
      <iframe
        title="La colonna sonora di Bologna — Spotify"
        style={{ display: "block", border: "none" }}
        src={SPOTIFY_EMBED_URL}
        width="100%"
        height="152"
        frameBorder="0"
        allowFullScreen=""
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        loading="lazy"
      />
      <a href={SPOTIFY_LINK_URL} target="_blank" rel="noreferrer"
        onClick={() => track("open_spotify_playlist")}
        style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "13px 12px", fontSize: 14, fontWeight: 700, color: BRAND.greenDark, textDecoration: "none" }}>
        {t.soundtrackOpen} ↗
      </a>
    </div>
  );
}

/* -------------------------------- DECK ------------------------------------ */
// Riga di card piccole, sempre in stile Airbnb Experience (come Esperienze):
// scroll orizzontale nativo (swipe naturale, niente drag/arrow custom).
// Il tap apre il dettaglio, dove restano prenotazione/local tip/itinerario.
function Deck({ items, lang, t, onDetail, itinerary, onToggleItin, isDocDeck }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, overflowX: "auto", paddingBottom: 4, marginInline: -18, paddingInline: 18 }} className="gl-exp-row">
      {items.map((p) => (
        <PlaceCard key={p.id} place={p} lang={lang} t={t} badge={isDocDeck ? t.docTitle : null}
          onClick={() => { track("view_card", { card: p.title_it || p.id, section: p.interests }); onDetail(p); }}
          inItin={itinerary.includes(p.id)} onToggleItin={() => onToggleItin(p.id)} />
      ))}
    </div>
  );
}

/* --------------------------- DETAIL GALLERY ------------------------------- */
function DetailGallery({ images, alt }) {
  const ref = useRef(null);
  const [idx, setIdx] = useState(0);
  const onScroll = () => { const el = ref.current; if (!el) return; setIdx(Math.round(el.scrollLeft / el.clientWidth)); };
  const single = images.length <= 1;
  return (
    <div style={{ position: "relative" }}>
      <div ref={ref} onScroll={onScroll} className="gl-gallery" style={single ? { overflow: "hidden" } : undefined}>
        {images.map((src, i) => (
          <img key={i} src={src} alt={`${alt} ${i + 1}`} className="gl-gallery-img" style={{ borderRadius: i === 0 ? "22px 22px 0 0" : 0 }} loading={i === 0 ? "eager" : "lazy"} />
        ))}
      </div>
      {!single && (
        <div style={{ position: "absolute", bottom: 54, left: 0, right: 0, display: "flex", justifyContent: "center", gap: 6, zIndex: 2, pointerEvents: "none" }}>
          {images.map((_, i) => (
            <span key={i} style={{ width: i === idx ? 18 : 6, height: 6, borderRadius: 999, background: i === idx ? "#fff" : "rgba(255,255,255,0.55)", transition: "all .2s" }} />
          ))}
        </div>
      )}
    </div>
  );
}

/* --------------------------- OPENING HOURS --------------------------------- */
function OpeningHours({ orari, t }) {
  const [open, setOpen] = useState(false);
  const rows = parseOrariRows(orari);
  if (rows.length === 0) return null;
  const status = getOpenStatus(orari);

  const label = !status
    ? t.hoursTitle
    : status.open
    ? (status.allDay ? t.open24h : `${t.openNow} · ${t.closesAt} ${status.closesAt}`)
    : status.opensAt
    ? `${t.closedNow} · ${t.opensAt} ${status.opensAt}`
    : t.closedNow;

  return (
    <div style={{ border: `1.5px solid ${BRAND.border}`, borderRadius: 14, padding: "13px 15px", marginBottom: 24 }}>
      <button onClick={() => setOpen((o) => !o)} style={{ all: "unset", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
        <span style={{ fontSize: 14.5, fontWeight: 600, color: status?.open ? BRAND.greenDark : BRAND.ink }}>🕒 {label}</span>
        <span style={{ fontSize: 13, color: BRAND.muted, transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }}>▾</span>
      </button>
      {open && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${BRAND.border}` }}>
          {rows.map((r, i) => <div key={i} style={{ fontSize: 13, color: "#4a463d", padding: "2px 0" }}>{r}</div>)}
          <p style={{ fontSize: 11, color: BRAND.muted, margin: "8px 0 0" }}>{t.hoursSynced}</p>
        </div>
      )}
    </div>
  );
}

/* --------------------------- DETAIL MODAL --------------------------------- */
// Effetto "Airbnb Experience" (parallax hero): mentre si scorre, la foto non
// si limita a scivolare via passivamente — si rimpicciolisce e sfuma più in
// fretta dello scroll stesso, "aspirata" verso l'alto, lasciando subito spazio
// al testo. Fatto leggendo scrollTop nello scroll handler e scrivendo
// transform/opacity direttamente sul nodo (via ref, senza re-render per
// frame: più fluido di farlo con lo stato). La freccia indietro resta fissa
// sopra a tutto e chiude il dettaglio tornando alle card, al posto della "×".
function DetailModal({ place, lang, t, onClose, onBook, onTip, onToggleItin, inItin, onOpenWeb, onBookIntent }) {
  const title = place[`title_${lang}`];
  const desc = place[`desc_${lang}`];
  const tip = place[`tip_${lang}`];
  const card = place.title_it || place.id;
  const methods = bookingMethods(place);
  // Condividi la card su WhatsApp: testo + link che riapre questo dettaglio
  const shareCard = () => {
    const url = `https://app.g-local.it/?place=${encodeURIComponent(place.id)}`;
    const text = `${t.shareCardText.replace("{title}", title)}\n${url}`;
    track("share_card", { card, method: "whatsapp" });
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };
  // un numero solo basta: WhatsApp usa contact, altrimenti phone; Chiama usa phone, altrimenti contact
  const waNumber = String(place.contact || place.phone || "").replace(/[^0-9]/g, "");
  const phoneNumber = String(place.phone || place.contact || "").replace(/[^0-9+]/g, "");
  // Menu nella lingua dell'utente: menu_it / menu_en; se manca, "menu"
  // (valido per tutte le lingue); se manca anche quello, l'altra lingua.
  const otherLang = lang === "it" ? "en" : "it";
  const menuUrls = [place[`menu_${lang}`], place.menu, place[`menu_${otherLang}`]]
    .map(splitList).find((l) => l.length > 0) || [];
  const extra = String(place.images || "").split(",").map((s) => s.trim()).filter(Boolean);
  const gallery = [place.image, ...extra].filter(Boolean);
  const address = String(place.address || "").trim();
  const mapsUrl = address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}` : null;

  const heroRef = useRef(null);
  const heroHeight = useRef(0);
  const onContentScroll = (e) => {
    const hero = heroRef.current;
    if (!hero) return;
    if (!heroHeight.current) heroHeight.current = hero.offsetHeight || 1;
    const y = e.currentTarget.scrollTop;
    const progress = Math.min(1, y / heroHeight.current); // 0 in cima, 1 appena la foto è "consumata"
    hero.style.opacity = String(1 - progress);
    hero.style.transform = `translateY(${-y * 0.55}px) scale(${1 - progress * 0.1})`;
  };

  return (
    <div onClick={onClose} className="gl-overlay" style={overlay}>
      <div onClick={(e) => e.stopPropagation()} className="gl-sheet" style={{ ...sheet, maxWidth: 540, padding: 0, maxHeight: "88vh", overflow: "hidden", position: "relative", display: "flex", flexDirection: "column" }}>
        <button onClick={onClose} aria-label={t.back} style={backBtn}>←</button>

        {/* flex:1 + minHeight:0 (non height:100%) è quello che permette a questo
            blocco di restare scrollabile dentro un contenitore con solo maxHeight */}
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch" }} onScroll={onContentScroll}>
          <div ref={heroRef} style={{ position: "relative", transformOrigin: "top center", willChange: "transform, opacity" }}>
            <DetailGallery images={gallery} alt={title} />
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(20,16,10,0.28), transparent 30%)", pointerEvents: "none" }} />
            <div style={{ position: "absolute", left: 20, bottom: 16, right: 20, pointerEvents: "none" }}>
              {place.location && <span style={{ display: "inline-block", fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#fff", opacity: 0.9, marginBottom: 6, textShadow: "0 1px 6px rgba(0,0,0,0.4)" }}>📍 {place.location}</span>}
              <h2 translate="no" className="notranslate" style={{ fontFamily: "'Fraunces', serif", fontWeight: 600, fontSize: "clamp(26px, 6vw, 34px)", lineHeight: 1.05, letterSpacing: "-0.02em", color: "#fff", margin: 0, textShadow: "0 1px 8px rgba(0,0,0,0.4)" }}>{title}</h2>
            </div>
          </div>

          <div style={{ position: "relative", background: BRAND.bg, padding: 22 }}>
            {place.price && <p style={{ fontSize: 22, fontWeight: 600, margin: "0 0 16px", color: BRAND.red, fontFamily: "'Fraunces', serif" }}>{displayPrice(place, lang)}</p>}
            <p style={{ fontSize: 16.5, lineHeight: 1.65, color: "#4a463d", margin: "0 0 20px", whiteSpace: "pre-line" }}>{desc}</p>

            {/* MENU — subito sotto la descrizione, solo se il posto ha un menu nel foglio */}
            {menuUrls.length > 0 && (
              <button onClick={() => {
                track("open_menu", { card });
                const allImages = menuUrls.every((u) => isImageUrl(u) || isCloudinaryPdf(u));
                onOpenWeb(allImages
                  ? { kind: "images", urls: menuUrls, title, label: t.menuTitle, card, purpose: "menu" }
                  : { kind: "iframe", url: menuEmbedUrl(menuUrls[0]), openUrl: menuUrls[0], title, label: t.menuTitle, card, purpose: "menu" });
              }} style={{ ...secondaryBtn, width: "100%", flex: "none", marginBottom: 20 }}>
                <span>📖</span>{t.menuBtn}
              </button>
            )}

            {tip && (
              <div style={{ background: "rgba(56,176,74,0.08)", border: `1.5px solid ${BRAND.green}`, borderRadius: 16, padding: "16px 18px", marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 16 }}>💬</span>
                  <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: BRAND.greenDark }}>{t.localTipTitle}</span>
                </div>
                <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: "#3c3826", whiteSpace: "pre-line" }}>{tip}</p>
              </div>
            )}

            {mapsUrl && (
              <a href={mapsUrl} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none", color: BRAND.ink, background: BRAND.card, border: `1.5px solid ${BRAND.border}`, borderRadius: 14, padding: "13px 15px", marginBottom: 24 }}>
                <span style={{ fontSize: 18 }}>📍</span>
                <span style={{ flex: 1, fontSize: 14.5, fontWeight: 500, lineHeight: 1.35 }}>{address}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: BRAND.green }}>{lang === "it" ? "Apri" : "Open"} →</span>
              </a>
            )}

            <OpeningHours orari={place.orari} t={t} />

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {(methods.includes("form") || methods.includes("sessioni")) && getDiscount(place) > 0 && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontSize: 14, fontWeight: 700, color: BRAND.greenDark }}>
                  <span style={{ background: BRAND.green, color: "#fff", borderRadius: 8, padding: "3px 8px", fontSize: 13 }}>-{getDiscount(place)}%</span>
                  {lang === "it" ? "prenotando con Glocal" : "when you book with Glocal"}
                </div>
              )}
              {(methods.includes("form") || methods.includes("sessioni")) && <button onClick={() => { track("start_booking", { card, method: methods[0], from: "detail" }); onBook(place); }} style={primaryBtn}>{methods.includes("sessioni") ? t.bookDates : t.book}</button>}
              {methods.includes("link") && (
                // La pagina di prenotazione del partner si apre in una nuova scheda e non più
                // nel pannello interno: dentro l'iframe, su iPhone, banner cookie, pagamenti e
                // link WhatsApp dei siti dei partner spesso non funzionano. Glocal resta aperta
                // nella scheda di prima, quindi l'utente ci torna con un tocco.
                <a href={withUtm(String(place.booking_url).trim())} target="_blank" rel="noopener noreferrer"
                  onClick={() => {
                    track("start_booking", { card, method: "link", from: "detail" });
                    onBookIntent?.();
                  }}
                  style={{ ...primaryBtn, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, textDecoration: "none", boxSizing: "border-box" }}>
                  {t.bookOnline}<span aria-hidden="true">↗</span>
                </a>
              )}
              {(methods.includes("whatsapp") || methods.includes("call")) && (
                <div style={{ display: "flex", gap: 10 }}>
                  {methods.includes("whatsapp") && (
                    <a href={`https://wa.me/${waNumber}?text=${encodeURIComponent(t.waBookText.replace("{title}", title))}`} target="_blank" rel="noreferrer"
                      onClick={() => { track("start_booking", { card, method: "whatsapp", from: "detail" }); onBookIntent?.(); }}
                      style={{ ...secondaryBtn, background: "#25D366", color: "#fff", border: "none" }}>
                      <span>💬</span>{t.bookWa}
                    </a>
                  )}
                  {methods.includes("call") && (
                    <a href={`tel:${phoneNumber}`}
                      onClick={() => { track("start_booking", { card, method: "call", from: "detail" }); onBookIntent?.(); }}
                      style={secondaryBtn}>
                      <span>📞</span>{t.bookCall}
                    </a>
                  )}
                </div>
              )}
              <button onClick={() => { if (!inItin) track("add_to_itinerary", { card: place.title_it || place.id, from: "detail" }); onToggleItin(place.id); }} style={{ width: "100%", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, background: inItin ? "rgba(56,176,74,0.12)" : "transparent", color: inItin ? BRAND.greenDark : BRAND.red, border: `1.5px solid ${inItin ? BRAND.green : BRAND.red}`, borderRadius: 14, padding: "14px", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                <PinIcon size={17} filled={inItin} color={inItin ? BRAND.greenDark : BRAND.red} />{inItin ? t.inItin : t.addItin}
              </button>
              <button onClick={shareCard} style={{ width: "100%", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, background: "#25D366", color: "#fff", border: "none", borderRadius: 14, padding: "14px", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                <span>💬</span>{t.shareWa}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* --------------------------- GOOGLE MAPS ROUTE ------------------------------ */
function googleMapsDirUrl(items) {
  // Usa l'indirizzo testuale (stessa fonte affidabile della card singola) e
  // ricade su lat/lng solo se un posto non ha l'address compilato.
  const stop = (p) => {
    const addr = String(p.address || "").trim();
    if (addr) return encodeURIComponent(addr);
    if (p.lat && p.lng) return `${p.lat},${p.lng}`;
    return null;
  };
  const pts = items.map((p) => ({ p, s: stop(p) })).filter((x) => x.s);
  if (pts.length === 0) return null;
  if (pts.length === 1) return `https://www.google.com/maps/search/?api=1&query=${pts[0].s}`;
  const origin = pts[0].s;
  const destination = pts[pts.length - 1].s;
  const waypoints = pts.slice(1, -1).map((x) => x.s).join("|");
  let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=walking`;
  if (waypoints) url += `&waypoints=${waypoints}`;
  return url;
}

/* --------------------------- ITINERARY TAB -------------------------------- */
function ItineraryTab({ t, lang, items, onRemove, onClear, onGoHome, onOpenDetail }) {
  const shareWhatsApp = () => {
    const lines = [`${t.itinTitle} — Bologna`, ""];
    items.forEach((p) => lines.push(`• ${p[`title_${lang}`]}${p.price ? ` (${displayPrice(p, lang)})` : ""}`));
    lines.push("");
    lines.push("📲 Scopri altre esperienze a Bologna: https://app.g-local.it");
    window.open(`https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
  };

  const mapsUrl = googleMapsDirUrl(items);

  return (
    <div className="gl-itin" style={{ padding: "20px 18px 0" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
        <h2 style={{ fontFamily: "'Fraunces', serif", fontWeight: 600, fontSize: 26, margin: 0, letterSpacing: "-0.01em" }}>{t.itinTitle}</h2>
        {items.length > 0 && <button onClick={onClear} style={{ background: "none", border: "none", color: BRAND.red, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>{t.clearAll}</button>}
      </div>

      {items.length === 0 ? (
        <EmptyState msg={t.itinEmpty} cta={t.goHome} onCta={onGoHome} icon="🗺️" />
      ) : (
        <>
          <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
            {mapsUrl && (
              <a href={mapsUrl} target="_blank" rel="noreferrer" style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, background: BRAND.card, color: BRAND.ink, textDecoration: "none", border: `1.5px solid ${BRAND.border}`, borderRadius: 14, padding: "13px 12px", fontSize: 14, fontWeight: 700 }}>
                <span>🗺️</span>{t.openInMaps}
              </a>
            )}
            <button onClick={shareWhatsApp} style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, background: "#25D366", color: "#fff", border: "none", borderRadius: 14, padding: "13px 12px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              <span>💬</span>{t.shareWa}
            </button>
          </div>

          <ul style={listReset}>
            {items.map((p) => (
              <li key={p.id} style={{ ...rowCard, cursor: "pointer" }} onClick={() => { track("view_card", { card: p.title_it || p.id, from: "itinerary" }); onOpenDetail(p); }}>
                <img src={p.image} alt="" style={{ width: 56, height: 56, borderRadius: 12, objectFit: "cover", flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div translate="no" className="notranslate" style={{ fontWeight: 600, fontSize: 15.5, lineHeight: 1.25 }}>{p[`title_${lang}`]}</div>
                  {p.location && <div style={{ fontSize: 12.5, color: BRAND.muted, marginTop: 1 }}>📍 {p.location}</div>}
                  {p.price && <div style={{ fontSize: 13.5, color: BRAND.red, marginTop: 2, fontWeight: 600 }}>{displayPrice(p, lang)}</div>}
                </div>
                <button onClick={(e) => { e.stopPropagation(); onRemove(p.id); }} aria-label={t.remove} style={rowX}>×</button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function EmptyState({ msg, cta, onCta, icon }) {
  return (
    <div style={{ textAlign: "center", padding: "48px 24px", color: BRAND.muted }}>
      <div style={{ fontSize: 40, marginBottom: 14 }}>{icon}</div>
      <p style={{ margin: "0 0 20px", fontSize: 15.5, lineHeight: 1.5, maxWidth: 320, marginInline: "auto" }}>{msg}</p>
      <button onClick={onCta} style={{ background: BRAND.green, color: "#fff", border: "none", borderRadius: 14, padding: "12px 24px", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>{cta}</button>
    </div>
  );
}

/* ------------------------------ TAB BAR ----------------------------------- */
function TabIcon({ name, active }) {
  const c = active ? "#e5383b" : "#7a7568"; const sw = 1.9;
  if (name === "home") return (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"><path d="M3 10.5 12 3l9 7.5"/><path d="M5 9.5V20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5"/><path d="M9.5 21v-6h5v6"/></svg>);
  return (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"><path d="M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1Z"/></svg>);
}

function TabBar({ t, tab, setTab, itinCount }) {
  const tabs = [{ id: "home", label: t.tabHome }, { id: "itin", label: t.tabItin, count: itinCount }];
  return (
    <nav className="gl-tabbar" style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 40, background: "rgba(251,248,240,0.96)", backdropFilter: "blur(12px)", borderTop: `1px solid ${BRAND.border}`, display: "flex", paddingBottom: "env(safe-area-inset-bottom, 0)" }}>
      {tabs.map((tb) => {
        const active = tab === tb.id;
        return (
          <button key={tb.id} onClick={() => setTab(tb.id)} style={{ flex: 1, background: "none", border: "none", cursor: "pointer", padding: "9px 8px 11px", display: "flex", flexDirection: "column", alignItems: "center", gap: 3, fontFamily: "inherit", color: active ? BRAND.red : BRAND.muted }}>
            <span style={{ position: "relative", display: "inline-flex" }}>
              <TabIcon name={tb.id} active={active} />
              {tb.count > 0 && <span style={{ position: "absolute", top: -5, right: -9, minWidth: 16, height: 16, borderRadius: 8, background: BRAND.green, color: "#fff", fontSize: 10.5, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 4px" }}>{tb.count}</span>}
            </span>
            <span style={{ fontSize: 11.5, fontWeight: active ? 700 : 500 }}>{tb.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

/* --------------------------- DESKTOP NAV ----------------------------------- */
// Su desktop (>= 900px) la TabBar in basso sparisce e Home/Itinerario
// diventano link nell'header, a sinistra del logo. Su mobile è nascosta.
function DesktopNav({ t, tab, setTab, itinCount }) {
  const tabs = [{ id: "home", label: t.tabHome }, { id: "itin", label: t.tabItin, count: itinCount }];
  return (
    <nav className="gl-desk-nav" aria-label="Menu">
      {tabs.map((tb) => {
        const active = tab === tb.id;
        return (
          <button key={tb.id} onClick={() => setTab(tb.id)} className={active ? "gl-desk-link is-active" : "gl-desk-link"}>
            <TabIcon name={tb.id} active={active} />
            <span>{tb.label}</span>
            {tb.count > 0 && <span className="gl-desk-count">{tb.count}</span>}
          </button>
        );
      })}
    </nav>
  );
}

/* BOOKING MODAL: vedi src/BookingModal.js (richiesta di prenotazione che passa da
   Glocal) e src/SessionBooking.js (tour/esperienze a date, booking = sessioni) */
function BookingSwitch(props) {
  return bookingMethods(props.place).includes("sessioni") ? <SessionBooking {...props} /> : <BookingModal {...props} />;
}

/* ------------------------- SKELETON / SPINNER ----------------------------- */
function DeckSkeleton() {
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ height: 26, width: 160, background: "#efe9db", borderRadius: 8, marginBottom: 14 }} className="gl-pulse" />
      <div style={{ aspectRatio: "4/3", background: "#efe9db", borderRadius: 22, marginBottom: 14 }} className="gl-pulse" />
      <div style={{ height: 48, background: "#efe9db", borderRadius: 14 }} className="gl-pulse" />
    </div>
  );
}
function Spinner() { return <span style={{ width: 14, height: 14, border: `2px solid ${BRAND.border}`, borderTopColor: BRAND.red, borderRadius: "50%", display: "inline-block" }} className="gl-spin" />; }

/* ----------------------------- SMALL BITS --------------------------------- */
const overlay = { position: "fixed", inset: 0, background: "rgba(26,20,12,0.55)", display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 90 };
const sheet = { background: BRAND.bg, width: "100%", borderRadius: "22px 22px 0 0", overflowY: "auto", maxHeight: "92vh", boxShadow: "0 -10px 50px rgba(0,0,0,0.25)" };
const inp = { width: "100%", boxSizing: "border-box", padding: "12px 14px", borderRadius: 12, border: `1.5px solid ${BRAND.border}`, background: BRAND.card, fontSize: 15, fontFamily: "inherit", color: BRAND.ink, outline: "none" };
const xBtn = { width: 44, height: 44, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(26,20,12,0.08)", border: "none", borderRadius: "50%", fontSize: 26, cursor: "pointer", color: BRAND.ink, lineHeight: 1 };
// Freccia "indietro" del DetailModal: fissa in alto a sinistra sopra al
// foglio (non dentro l'area che scorre), così resta visibile mentre la foto
// scorre via dietro di lei.
// position:"fixed" (non "absolute") + zIndex sopra l'overlay (90): così il
// pulsante resta sempre visibile e cliccabile sullo schermo, qualsiasi cosa
// succeda con lo scroll del foglio sotto — su alcuni iPhone/Safari un bottone
// "absolute" dentro un contenitore che scorre può finire coperto durante lo
// scroll. env(safe-area-inset-top) lo tiene lontano dalla notch/isola dinamica.
const backBtn = { position: "fixed", top: "calc(14px + env(safe-area-inset-top, 0px))", left: 14, zIndex: 95, width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.92)", border: "none", borderRadius: "50%", fontSize: 19, fontWeight: 700, cursor: "pointer", color: BRAND.ink, lineHeight: 1, boxShadow: "0 3px 12px rgba(0,0,0,0.22)" };
const primaryBtn = { width: "100%", background: BRAND.green, color: "#fff", border: "none", borderRadius: 14, padding: 16, fontSize: 16, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" };
const secondaryBtn = { flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, background: BRAND.card, color: BRAND.ink, textDecoration: "none", border: `1.5px solid ${BRAND.border}`, borderRadius: 14, padding: "14px 12px", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", boxSizing: "border-box" };
const sheetLabel = { fontSize: 12.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: BRAND.muted, margin: "0 0 12px" };
const listReset = { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10 };
const rowCard = { display: "flex", gap: 13, alignItems: "center", background: BRAND.card, border: `1px solid ${BRAND.border}`, borderRadius: 14, padding: 11 };
const rowX = { background: "none", border: "none", color: "#bbb", fontSize: 22, cursor: "pointer", flexShrink: 0, lineHeight: 1 };

function Field({ label, children, flex }) {
  return (<label style={{ display: "block", marginBottom: 14, flex: flex ? 1 : undefined }}><span style={{ display: "block", fontSize: 12.5, fontWeight: 700, marginBottom: 6, color: "#3a3630" }}>{label}</span>{children}</label>);
}
// Lingua iniziale dal browser: qualsiasi variante di italiano → "it", il resto → "en".
function detectLang() {
  try {
    const list = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || ""];
    return String(list[0] || "").toLowerCase().startsWith("it") ? "it" : "en";
  } catch { return "it"; }
}

// Selettore lingua: segmented control con icona globo + nome per esteso
// ("Italiano" / "English") al posto di bandiera + sigla — le bandiere emoji su
// Windows non si vedono (diventano "IT"/"GB") e la sigla grigia sembrava
// disattivata. Su schermi stretti restano le sigle.
function LangToggle({ lang, setLang }) {
  const LABEL = { it: "Italiano", en: "English" };
  const choose = (l) => { if (l !== lang) { track("change_language", { language: l }); setLang(l); } };
  return (
    <div role="group" aria-label="Lingua / Language" style={{ display: "flex", alignItems: "center", gap: 2, background: BRAND.card, border: `1.5px solid ${BRAND.border}`, borderRadius: 999, padding: 3 }}>
      <span aria-hidden="true" style={{ display: "inline-flex", padding: "0 4px 0 6px", color: BRAND.muted }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M3 12h18"/><path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18Z"/></svg>
      </span>
      {["it", "en"].map((l) => {
        const active = lang === l;
        return (
          <button key={l} onClick={() => choose(l)} aria-pressed={active} lang={l} title={LABEL[l]}
            style={{ border: "none", cursor: active ? "default" : "pointer", borderRadius: 999, padding: "6px 11px", fontSize: 13, fontWeight: 700, fontFamily: "inherit", background: active ? BRAND.ink : "transparent", color: active ? "#fff" : BRAND.ink, transition: "background .15s" }}>
            <span className="gl-lang-long">{LABEL[l]}</span><span className="gl-lang-short">{l.toUpperCase()}</span>
          </button>
        );
      })}
    </div>
  );
}

/* --------------------------- WEB SHEET (menu / prenotazione) --------------- */
// Popup a tutta altezza dentro l'app. kind "images": immagini impilate
// (menu su Cloudinary). kind "iframe": pagina esterna (menu online o pagina
// di prenotazione del gestionale). Alcuni siti vietano di essere incorporati
// (X-Frame-Options): per questo c'è sempre "Apri in una nuova scheda" in cima.
// Sta sopra al DetailModal (zIndex 100 > 95 della freccia indietro).
function WebSheet({ sheetData, t, onClose }) {
  const { kind, url, urls = [], title, label, card, purpose } = sheetData;
  const openUrl = sheetData.openUrl || (kind === "images" ? urls[0] : url);
  return (
    <div onClick={onClose} className="gl-overlay" style={{ ...overlay, zIndex: 100 }}>
      <div onClick={(e) => e.stopPropagation()} className="gl-sheet" style={{ ...sheet, maxWidth: 640, height: "92vh", padding: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px 12px", borderBottom: `1px solid ${BRAND.border}`, flexShrink: 0 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11.5, textTransform: "uppercase", letterSpacing: "0.14em", color: BRAND.green, fontWeight: 700 }}>{label}</div>
            <div translate="no" className="notranslate" style={{ fontFamily: "'Fraunces', serif", fontWeight: 600, fontSize: 19, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</div>
          </div>
          <button onClick={onClose} aria-label={t.close} style={xBtn}>×</button>
        </div>
        {kind === "iframe" && (
          <a href={openUrl} target="_blank" rel="noreferrer" onClick={() => track("open_external_tab", { card, purpose })}
            style={{ display: "block", flexShrink: 0, padding: "8px 16px", fontSize: 12.5, color: BRAND.muted, textDecoration: "none", background: "rgba(26,20,12,0.04)", borderBottom: `1px solid ${BRAND.border}` }}>
            {t.embedHint} <span style={{ color: BRAND.greenDark, fontWeight: 700 }}>{t.openNewTab} ↗</span>
          </a>
        )}
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch", background: kind === "iframe" ? "#fff" : BRAND.bg }}>
          {kind === "images"
            ? urls.map((src, i) => isCloudinaryPdf(src) ? <PdfPages key={i} url={src} label={label} /> : <img key={i} src={src} alt={`${label} ${i + 1}`} loading={i === 0 ? "eager" : "lazy"} style={{ display: "block", width: "100%", height: "auto", borderBottom: `1px solid ${BRAND.border}` }} />)
            : <iframe title={`${label} — ${title}`} src={url} style={{ display: "block", width: "100%", height: "100%", border: "none" }} />}
        </div>
      </div>
    </div>
  );
}

/* --------------------------- LOCAL TIP SHEET ------------------------------ */
// struttura IDENTICA al BookingModal: stesso overlay, stesso sheet, stessa impaginazione
function LocalTipSheet({ place, tip, lang, t, onClose }) {
  const title = place[`title_${lang}`];
  return (
    <div onClick={onClose} className="gl-overlay" style={overlay}>
      <div onClick={(e) => e.stopPropagation()} className="gl-sheet" style={{ ...sheet, maxWidth: 520, padding: 24, minHeight: "55vh" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
          <span style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.14em", color: BRAND.green, fontWeight: 700 }}>{t.localTip}</span>
          <button onClick={onClose} style={xBtn}>×</button>
        </div>
        <h3 translate="no" className="notranslate" style={{ fontFamily: "'Fraunces', serif", fontWeight: 600, fontSize: 23, margin: "0 0 20px" }}>{title}</h3>
        <p style={{ fontSize: 16.5, lineHeight: 1.65, color: "#3a3630", margin: 0, whiteSpace: "pre-line" }}>{tip}</p>
      </div>
    </div>
  );
}

function FontLink() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600&family=Archivo:wdth,wght@62..125,400..900&display=swap');
      * { -webkit-tap-highlight-color: transparent; box-sizing: border-box; }
      body { margin: 0; }
      button:focus-visible, input:focus-visible, textarea:focus-visible { outline: 2px solid ${BRAND.green}; outline-offset: 2px; }
      input:focus, textarea:focus { border-color: ${BRAND.green} !important; }
      .gl-gallery { display: flex; overflow-x: auto; scroll-snap-type: x mandatory; scrollbar-width: none; -ms-overflow-style: none; }
      .gl-gallery::-webkit-scrollbar { display: none; }
      .gl-gallery-img { flex: 0 0 100%; width: 100%; aspect-ratio: 4/3; object-fit: cover; display: block; scroll-snap-align: center; }
      .gl-card { transition: transform .15s ease, box-shadow .15s ease; }
      .gl-card:active { transform: scale(0.975) translateY(1px); box-shadow: 0 2px 10px rgba(40,30,15,0.10) !important; }
      .gl-pick-card { opacity: 0; animation: glFadeUp .5s ease forwards; }
      @keyframes glFadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
      .gl-check-pop { animation: glCheckPop .35s cubic-bezier(.34,1.56,.64,1); }
      @keyframes glCheckPop { from { transform: scale(0); opacity: 0; } to { transform: scale(1); opacity: 1; } }
      .gl-rotator-img { animation: glFade 2.6s ease-in-out; }
      @keyframes glFade { 0% { opacity: 0; } 15% { opacity: 1; } 85% { opacity: 1; } 100% { opacity: 0; } }
      .gl-rotator-wrap { position: fixed; z-index: 6; pointer-events: none; animation: glRoam 15s ease-in-out infinite; }
      @keyframes glRoam {
        0%   { top: 16%; left: 8%; }
        20%  { top: 9%;  left: 66%; }
        40%  { top: 58%; left: 80%; }
        60%  { top: 74%; left: 18%; }
        80%  { top: 40%; left: 50%; }
        100% { top: 16%; left: 8%; }
      }
      .gl-rotator-float { animation: glOrbit 6.5s ease-in-out infinite; }
      @keyframes glOrbit { 0% { transform: rotate(0deg); } 25% { transform: rotate(5deg); } 50% { transform: rotate(-4deg); } 75% { transform: rotate(4deg); } 100% { transform: rotate(0deg); } }
      .gl-pulse { animation: glpulse 1.4s ease-in-out infinite; }
      .gl-noscrollbar::-webkit-scrollbar { display: none; }
      @keyframes glpulse { 0%,100% { opacity: 1 } 50% { opacity: 0.5 } }
      .gl-spin { animation: glspin 0.8s linear infinite; }
      @keyframes glspin { to { transform: rotate(360deg) } }
      .gl-lang-short { display: none; }
      @media (max-width: 560px) { .gl-lang-long { display: none; } .gl-lang-short { display: inline; } }
      /* ---------- DESKTOP (>= 900px) ---------- */
      .gl-desk-nav { display: none; }
      @media (min-width: 900px) {
        .gl-tabbar { display: none !important; }
        .gl-desk-nav { display: flex; gap: 6px; }
        .gl-desk-link { display: inline-flex; align-items: center; gap: 7px; background: none; border: none; border-radius: 999px; padding: 8px 14px; font-family: inherit; font-size: 14.5px; font-weight: 600; color: ${BRAND.muted}; cursor: pointer; transition: background .15s, color .15s; }
        .gl-desk-link svg { width: 19px; height: 19px; }
        .gl-desk-link:hover { background: rgba(26,20,12,0.06); color: ${BRAND.ink}; }
        .gl-desk-link.is-active { color: ${BRAND.red}; background: rgba(229,56,59,0.08); }
        .gl-desk-count { min-width: 18px; height: 18px; border-radius: 9px; background: ${BRAND.green}; color: #fff; font-size: 11px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; padding: 0 5px; }

        .gl-main { max-width: 1180px !important; padding-bottom: 72px !important; }
        .gl-home { padding: 24px 40px 0 !important; }
        .gl-home-title { font-size: 56px !important; margin-top: 18px !important; }
        .gl-itin { max-width: 760px; margin: 0 auto; padding: 36px 40px 0 !important; }

        .gl-chip-row { flex-wrap: wrap; overflow: visible !important; margin-inline: 0 !important; padding-inline: 0 !important; }

        /* righe di card: da carosello orizzontale a griglia */
        .gl-exp-row { display: grid !important; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 28px 20px !important; overflow: visible !important; margin-inline: 0 !important; padding-inline: 0 !important; }
        .gl-place-card { width: 100% !important; min-width: 0 !important; max-width: none !important; }
        .gl-place-img img { transition: transform .35s ease; }
        .gl-place-card:hover .gl-place-img img { transform: scale(1.04); }

        /* eventi + guida affiancati */
        .gl-promo-row { display: grid; grid-template-columns: 1fr 1.4fr; gap: 20px; margin-top: 32px; align-items: stretch; }
        .gl-promo-row > * { margin-top: 0 !important; height: 100%; }
        .gl-promo-row .gl-ev-strip { flex-direction: column; align-items: flex-start; justify-content: space-between; padding: 22px !important; gap: 16px; }

        .gl-itin-fab { right: 32px !important; bottom: 32px !important; }
        .gl-ask-fab { left: 32px !important; bottom: 32px !important; }
        .gl-ev-ticker { left: auto !important; right: 32px !important; bottom: 32px !important; width: 440px; margin: 0 !important; }

        /* modali: centrati invece che bottom-sheet */
        .gl-overlay, .gl-ev-overlay { align-items: center !important; padding: 24px; }
        .gl-sheet, .gl-ev-sheet { border-radius: 22px !important; max-height: 88vh; box-shadow: 0 24px 70px rgba(0,0,0,0.3) !important; }
      }
      @media (prefers-reduced-motion: reduce) { *, .gl-pulse, .gl-spin, .gl-card, .gl-pick-card, .gl-check-pop, .gl-rotator-img, .gl-rotator-float, .gl-rotator-wrap { animation: none !important; transition: none !important; opacity: 1 !important; transform: none !important; } }
    `}</style>
  );
}
